// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  eyebrowShare: 'Share',
  eyebrowShared: 'Shared with you',
  close: 'Close',
  share: 'Share',
  shared: 'Shared',
  shareWith: 'Share: {audience}',
  whoTitle: 'Who can open it',
  onlyYou: 'Only you',
  onlyYouBody: 'Nobody else can open it',
  everyone: 'Everyone at {company}',
  everyoneBody: 'Anyone in the company can open it',
  specificPeople: 'Specific people',
  specificPeopleBody: 'Choose who can open it',
  companyNeedsEdit: 'Only people who can edit it can share it with everyone.',
  privateNeedsOwner: 'Only its owner or an admin can make it private again.',
  skillCompany: 'A Skill reaches everyone when it is published.',
  peopleCount: 'one:person|other:people',
  addPeople: 'Add people by name or email',
  noMatches: 'No colleague matches that.',
  noColleagues: 'No other colleagues have joined yet.',
  loadingPeople: 'Loading colleagues…',
  peopleFailed: 'We couldn’t load your colleagues. Close and try again.',
  owner: 'Owner',
  you: '{name} (you)',
  canUse: 'Can use',
  canUseBody: 'Open it and use the current version.',
  canEdit: 'Can edit',
  canEditBody: 'Change it and what it runs on, through its chat.',
  canView: 'Can view',
  canViewBody: 'Read it and follow new answers.',
  canEditFile: 'Change it.',
  removeAccess: 'Remove access',
  accessFor: 'Access for {name}',
  everyoneRowBody: 'Anyone in the company can find and open it',
  yourAccess: 'Your access',
  sharedBy: '{name} shared it with you',
  sharedWithYou: 'It was shared with you',
  ownerDecides: '{name} owns it and decides who can open it.',
  runsOn: 'Runs on',
  runsOnHelp:
    'These connections belong to {thing}. People who open it see what it shows, but never get the accounts.',
  runsOnViewerHelp:
    'You use these connections through {thing}. You don’t get the accounts, and it never uses yours.',
  runsOnEmpty: 'It doesn’t use any connections yet.',
  runsOnFailed: 'We couldn’t load what it runs on.',
  yours: 'Yours',
  from: 'From {name}',
  fromColleague: 'From a colleague',
  reads: 'Reads {list}',
  readsNothing: 'Reads nothing yet',
  canChange: 'can make changes',
  paused: 'Paused',
  pausedBody:
    '{name}’s access ended, so {thing} can’t read {connection} for anyone right now. It won’t switch to someone else’s account on its own.',
  pausedByColleague:
    'The access it used has ended, so {thing} can’t read {connection} for anyone right now. It won’t switch to someone else’s account on its own.',
  fixInChat: 'Fix in chat',
  fixInChatBody: 'Ask Chat to use your own access instead.',
  chatHint: 'To add or change a connection, ask in its chat.',
  changeWithChat: 'Change with Chat',
  editInChat: 'Edit in chat',
  unnamedConnection: 'Unnamed connection',
  retry: 'Try again',
  cancel: 'Cancel',
  save: 'Save',
  done: 'Done',
  saving: 'Saving…',
  statusPrivate: 'Only you can open it.',
  statusCompany: 'Everyone at {company} can open it.',
  statusPeople: 'Shared with {people}.',
  statusPeopleEmpty: 'Add people to share it.',
  statusPending: 'Nothing changes until you save.',
  theApp: 'the App',
  theAgent: 'the Agent',
  theSkill: 'the Skill',
  theResult: 'the result',
  theItem: 'it',
  untitled: 'Untitled',
  updated: 'Sharing updated.',
  agentNoOwner: 'Its owner left, so it stays shared; switch it off or archive it instead.',
  agentPersonal:
    'Only you makes it private to its owner: it switches off, waiting runs end, and its next run starts a new Result.',
  changesPrompt:
    'Help me change what {kind} {id} ({name}) runs on. Read its connections first, then propose the change for me to approve.',
  pausedCodeChanged:
    'Someone changed {thing} after {name} connected {connection}. It’s paused for everyone until {name} confirms the connection in its chat.',
  pausedCodeChangedColleague:
    'Someone changed {thing} after its connection to {connection} was confirmed. It’s paused for everyone until the connection is confirmed again in its chat.',
  pausedCodeChangedYou:
    'Someone changed {thing} after you connected {connection}. It’s paused for everyone until you confirm the connection in its chat.',
  fixInChatConfirmBody: 'Ask Chat to confirm it with your access.',
};

const copy: NamespaceCatalog<keyof typeof en> = {
  bg: {
    eyebrowShare: 'Споделяне',
    eyebrowShared: 'Споделено с вас',
    close: 'Затвори',
    share: 'Споделяне',
    shared: 'Споделено',
    shareWith: 'Споделяне: {audience}',
    whoTitle: 'Кой може да го отвори',
    onlyYou: 'Само вие',
    onlyYouBody: 'Никой друг не може да го отвори',
    everyone: 'Всички в {company}',
    everyoneBody: 'Всеки в компанията може да го отвори',
    specificPeople: 'Конкретни хора',
    specificPeopleBody: 'Изберете кой може да го отвори',
    companyNeedsEdit: 'Само хората, които могат да го редактират, могат да го споделят с всички.',
    privateNeedsOwner: 'Само собственикът или администратор може отново да го направи частно.',
    skillCompany: 'Умение става достъпно за всички, когато бъде публикувано.',
    peopleCount: 'one:човек|other:души',
    addPeople: 'Добавете хора по име или имейл',
    noMatches: 'Няма колега, който да отговаря на това.',
    noColleagues: 'Все още няма други присъединили се колеги.',
    loadingPeople: 'Зареждане на колегите…',
    peopleFailed: 'Не успяхме да заредим колегите ви. Затворете и опитайте отново.',
    owner: 'Собственик',
    you: '{name} (вие)',
    canUse: 'Може да използва',
    canUseBody: 'Да го отваря и да използва текущата версия.',
    canEdit: 'Може да редактира',
    canEditBody: 'Да го променя и да променя върху какво се изпълнява чрез неговия чат.',
    canView: 'Може да преглежда',
    canViewBody: 'Да го чете и да следи новите отговори.',
    canEditFile: 'Да го променя.',
    removeAccess: 'Премахни достъпа',
    accessFor: 'Достъп за {name}',
    everyoneRowBody: 'Всеки в компанията може да го намери и отвори',
    yourAccess: 'Вашият достъп',
    sharedBy: '{name} го сподели с вас',
    sharedWithYou: 'То беше споделено с вас',
    ownerDecides: '{name} е негов собственик и решава кой може да го отвори.',
    runsOn: 'Изпълнява се върху',
    runsOnHelp:
      'Тези връзки принадлежат на {thing}. Хората, които го отворят, виждат какво показва, но никога не получават достъп до акаунтите.',
    runsOnViewerHelp:
      'Използвате тези връзки чрез {thing}. Не получавате достъп до акаунтите и то никога не използва вашите.',
    runsOnEmpty: 'Все още не използва връзки.',
    runsOnFailed: 'Не успяхме да заредим върху какво се изпълнява.',
    yours: 'Ваше',
    from: 'От {name}',
    fromColleague: 'От колега',
    reads: 'Чете {list}',
    readsNothing: 'Все още не чете нищо',
    canChange: 'може да прави промени',
    paused: 'На пауза',
    pausedBody:
      'Достъпът на {name} е изтекъл, затова {thing} в момента не може да чете {connection} за никого. Няма автоматично да премине към акаунта на друг човек.',
    pausedByColleague:
      'Използваният достъп е изтекъл, затова {thing} в момента не може да чете {connection} за никого. Няма автоматично да премине към акаунта на друг човек.',
    fixInChat: 'Коригирайте в чата',
    fixInChatBody: 'Помолете Chat да използва вашия собствен достъп.',
    chatHint: 'За да добавите или промените връзка, попитайте в неговия чат.',
    changeWithChat: 'Променете с Chat',
    editInChat: 'Редактирайте в чата',
    unnamedConnection: 'Връзка без име',
    retry: 'Опитайте отново',
    cancel: 'Отказ',
    save: 'Запази',
    done: 'Готово',
    saving: 'Запазване…',
    statusPrivate: 'Само вие можете да го отворите.',
    statusCompany: 'Всеки в {company} може да го отвори.',
    statusPeople: 'Споделено с {people}.',
    statusPeopleEmpty: 'Добавете хора, за да го споделите.',
    statusPending: 'Нищо няма да се промени, докато не запазите.',
    theApp: 'приложението',
    theAgent: 'агентът',
    theSkill: 'умението',
    theResult: 'резултатът',
    theItem: 'то',
    untitled: 'Без заглавие',
    updated: 'Споделянето е обновено.',
    agentNoOwner:
      'Собственикът напусна, затова агентът остава споделен. Изключете го или го архивирайте.',
    agentPersonal:
      'Тази опция прави агента личен за собственика му: той се изключва, чакащите изпълнения приключват и следващото започва нов резултат.',
    changesPrompt:
      'Помогни ми да променя върху какво се изпълнява {kind} {id} ({name}). Първо прочети връзките му, после предложи промяната, за да я одобря.',
    pausedCodeChanged:
      'Някой промени {thing}, след като {name} свърза {connection}. На пауза е за всички, докато {name} не потвърди връзката в неговия чат.',
    pausedCodeChangedColleague:
      'Някой промени {thing}, след като връзката му с {connection} беше потвърдена. На пауза е за всички, докато връзката не бъде потвърдена отново в неговия чат.',
    pausedCodeChangedYou:
      'Някой промени {thing}, след като вие свързахте {connection}. На пауза е за всички, докато не потвърдите връзката в неговия чат.',
    fixInChatConfirmBody: 'Помолете Chat да го потвърди с вашия достъп.',
  },
  hr: {
    eyebrowShare: 'Dijeli',
    eyebrowShared: 'Podijeljeno s vama',
    close: 'Zatvori',
    share: 'Dijeli',
    shared: 'Dijeljeno',
    shareWith: 'Dijeli: {audience}',
    whoTitle: 'Tko ga može otvoriti',
    onlyYou: 'Samo vi',
    onlyYouBody: 'Nitko ga drugi ne može otvoriti',
    everyone: 'Svi u tvrtki {company}',
    everyoneBody: 'Svatko u tvrtki može ga otvoriti',
    specificPeople: 'Određene osobe',
    specificPeopleBody: 'Odaberite tko ga može otvoriti',
    companyNeedsEdit: 'Samo ga osobe koje ga mogu uređivati mogu podijeliti sa svima.',
    privateNeedsOwner: 'Samo ga vlasnik ili administrator može ponovno učiniti privatnim.',
    skillCompany: 'Vještina postaje dostupna svima kada se objavi.',
    peopleCount: 'one:osoba|few:osobe|other:osoba',
    addPeople: 'Dodajte osobe prema imenu ili e-adresi',
    noMatches: 'Nema kolege koji odgovara tom kriteriju.',
    noColleagues: 'Još se nije pridružio nijedan drugi kolega.',
    loadingPeople: 'Učitavanje kolega…',
    peopleFailed: 'Nismo mogli učitati vaše kolege. Zatvorite i pokušajte ponovno.',
    owner: 'Vlasnik',
    you: '{name} (vi)',
    canUse: 'Može koristiti',
    canUseBody: 'Otvoriti ga i koristiti trenutačnu verziju.',
    canEdit: 'Može uređivati',
    canEditBody: 'Mijenjati ga i ono na čemu radi putem njegova chata.',
    canView: 'Može pregledavati',
    canViewBody: 'Čitati ga i pratiti nove odgovore.',
    canEditFile: 'Može ga mijenjati.',
    removeAccess: 'Ukloni pristup',
    accessFor: 'Pristup za {name}',
    everyoneRowBody: 'Svatko u tvrtki može ga pronaći i otvoriti',
    yourAccess: 'Vaš pristup',
    sharedBy: '{name} podijelio/la je to s vama',
    sharedWithYou: 'Podijeljeno je s vama',
    ownerDecides: '{name} je vlasnik i odlučuje tko ga može otvoriti.',
    runsOn: 'Radi na',
    runsOnHelp:
      'Te veze pripadaju stavci {thing}. Osobe koje je otvore vide što prikazuje, ali nikad ne dobivaju pristup računima.',
    runsOnViewerHelp:
      'Te veze koristite putem stavke {thing}. Ne dobivate pristup računima, a ona nikad ne koristi vaše.',
    runsOnEmpty: 'Još ne koristi nijednu vezu.',
    runsOnFailed: 'Nismo mogli učitati podatke o tome na čemu radi.',
    yours: 'Vaše',
    from: 'Od osobe {name}',
    fromColleague: 'Od kolege',
    reads: 'Čita {list}',
    readsNothing: 'Još ništa ne čita',
    canChange: 'može mijenjati',
    paused: 'Pauzirano',
    pausedBody:
      'Pristup osobe {name} je završio pa {thing} trenutačno ni za koga ne može čitati {connection}. Neće samostalno prijeći na račun druge osobe.',
    pausedByColleague:
      'Pristup koji je koristio završio je pa {thing} trenutačno ni za koga ne može čitati {connection}. Neće samostalno prijeći na račun druge osobe.',
    fixInChat: 'Popravi u chatu',
    fixInChatBody: 'Zatražite od Chata da umjesto toga koristi vaš vlastiti pristup.',
    chatHint: 'Da biste dodali ili promijenili vezu, zatražite to u njegovu chatu.',
    changeWithChat: 'Promijeni putem Chata',
    editInChat: 'Uredi u chatu',
    unnamedConnection: 'Neimenovana veza',
    retry: 'Pokušaj ponovno',
    cancel: 'Odustani',
    save: 'Spremi',
    done: 'Gotovo',
    saving: 'Spremanje…',
    statusPrivate: 'Samo ga vi možete otvoriti.',
    statusCompany: 'Svatko u tvrtki {company} može ga otvoriti.',
    statusPeople: 'Podijeljeno s osobama: {people}.',
    statusPeopleEmpty: 'Dodajte osobe da biste to podijelili.',
    statusPending: 'Ništa se neće promijeniti dok ne spremite.',
    theApp: 'Aplikacija',
    theAgent: 'Agent',
    theSkill: 'Vještina',
    theResult: 'rezultat',
    theItem: 'to',
    untitled: 'Bez naslova',
    updated: 'Dijeljenje je ažurirano.',
    agentNoOwner: 'Vlasnik je otišao, pa agent ostaje dijeljen. Isključite ga ili arhivirajte.',
    agentPersonal:
      'Ova opcija čini agenta privatnim za njegova vlasnika: isključuje se, izvršavanja na čekanju završavaju i sljedeće započinje novi rezultat.',
    changesPrompt:
      'Pomozite mi promijeniti na čemu se izvršava {kind} {id} ({name}). Najprije pročitajte njegove veze, a zatim predložite promjenu kako bih je mogao odobriti.',
    pausedCodeChanged:
      'Netko je promijenio {thing} nakon što je {name} povezao/la {connection}. Pauzirano je za sve dok {name} ne potvrdi vezu u njegovu chatu.',
    pausedCodeChangedColleague:
      'Netko je promijenio {thing} nakon što je potvrđena njegova veza s {connection}. Pauzirano je za sve dok se veza ponovno ne potvrdi u njegovu chatu.',
    pausedCodeChangedYou:
      'Netko je promijenio {thing} nakon što ste povezali {connection}. Pauzirano je za sve dok vi ne potvrdite vezu u njegovu chatu.',
    fixInChatConfirmBody: 'Zatražite od Chata da to potvrdi s vašim pristupom.',
  },
  cs: {
    eyebrowShare: 'Sdílet',
    eyebrowShared: 'Sdíleno s vámi',
    close: 'Zavřít',
    share: 'Sdílet',
    shared: 'Sdílená',
    shareWith: 'Sdílet: {audience}',
    whoTitle: 'Kdo to může otevřít',
    onlyYou: 'Jen vy',
    onlyYouBody: 'Nikdo jiný to nemůže otevřít',
    everyone: 'Všichni ve firmě {company}',
    everyoneBody: 'Kdokoli ve společnosti to může otevřít',
    specificPeople: 'Konkrétní lidé',
    specificPeopleBody: 'Vyberte, kdo to může otevřít',
    companyNeedsEdit: 'Sdílet to se všemi mohou pouze lidé, kteří to mohou upravovat.',
    privateNeedsOwner: 'Znovu to nastavit jako soukromé může pouze vlastník nebo správce.',
    skillCompany: 'Dovednost se dostane ke všem po zveřejnění.',
    peopleCount: 'one:člověk|few:lidé|many:lidí|other:lidí',
    addPeople: 'Přidat lidi podle jména nebo e-mailu',
    noMatches: 'Tomu neodpovídá žádný kolega.',
    noColleagues: 'Zatím se nepřipojili žádní další kolegové.',
    loadingPeople: 'Načítání kolegů…',
    peopleFailed: 'Nepodařilo se načíst vaše kolegy. Zavřete okno a zkuste to znovu.',
    owner: 'Vlastník',
    you: '{name} (vy)',
    canUse: 'Může používat',
    canUseBody: 'Otevřít a používat aktuální verzi.',
    canEdit: 'Může upravovat',
    canEditBody: 'Měnit ji a to, na čem běží, prostřednictvím jejího chatu.',
    canView: 'Může zobrazit',
    canViewBody: 'Číst ji a sledovat nové odpovědi.',
    canEditFile: 'Měnit ji.',
    removeAccess: 'Odebrat přístup',
    accessFor: 'Přístup pro {name}',
    everyoneRowBody: 'Kdokoli ve společnosti to může najít a otevřít',
    yourAccess: 'Váš přístup',
    sharedBy: '{name} s vámi sdílel(a) tuto položku',
    sharedWithYou: 'Bylo to s vámi sdíleno',
    ownerDecides: '{name} je vlastníkem a rozhoduje, kdo to může otevřít.',
    runsOn: 'Využívá',
    runsOnHelp:
      'Tato připojení patří k položce {thing}. Lidé, kteří ji otevřou, uvidí, co zobrazuje, ale nikdy nezískají přístup k účtům.',
    runsOnViewerHelp:
      'Tato připojení používáte prostřednictvím položky {thing}. Přístup k účtům nezískáte a položka nikdy nepoužije vaše účty.',
    runsOnEmpty: 'Zatím nepoužívá žádná připojení.',
    runsOnFailed: 'Nepodařilo se načíst, co položka využívá.',
    yours: 'Vaše',
    from: 'Od {name}',
    fromColleague: 'Od kolegy',
    reads: 'Čte {list}',
    readsNothing: 'Zatím nic nečte',
    canChange: 'může provádět změny',
    paused: 'Pozastaveno',
    pausedBody:
      'Přístup uživatele {name} skončil, takže {thing} teď nemůže pro nikoho číst {connection}. Samo nepřejde na účet někoho jiného.',
    pausedByColleague:
      'Přístup, který položka používala, skončil, takže {thing} teď nemůže pro nikoho číst {connection}. Samo nepřejde na účet někoho jiného.',
    fixInChat: 'Opravit v chatu',
    fixInChatBody: 'Požádejte Chat, aby místo toho použil váš vlastní přístup.',
    chatHint: 'Chcete-li přidat nebo změnit připojení, požádejte o to v chatu této položky.',
    changeWithChat: 'Změnit pomocí Chatu',
    editInChat: 'Upravit v chatu',
    unnamedConnection: 'Nepojmenované připojení',
    retry: 'Zkusit znovu',
    cancel: 'Zrušit',
    save: 'Uložit',
    done: 'Hotovo',
    saving: 'Ukládání…',
    statusPrivate: 'Otevřít to můžete pouze vy.',
    statusCompany: 'Kdokoli ve společnosti {company} to může otevřít.',
    statusPeople: 'Sdíleno s uživateli {people}.',
    statusPeopleEmpty: 'Přidejte lidi, se kterými to chcete sdílet.',
    statusPending: 'Dokud změny neuložíte, nic se nezmění.',
    theApp: 'aplikace',
    theAgent: 'agent',
    theSkill: 'dovednost',
    theResult: 'výsledek',
    theItem: 'to',
    untitled: 'Bez názvu',
    updated: 'Sdílení bylo aktualizováno.',
    agentNoOwner: 'Vlastník odešel, proto agent zůstává sdílený. Vypněte ho nebo archivujte.',
    agentPersonal:
      'Tato volba nastaví agenta jako soukromého pro jeho vlastníka: vypne se, čekající běhy skončí a další běh vytvoří nový výsledek.',
    changesPrompt:
      'Pomoz mi změnit, na čem {kind} {id} ({name}) běží. Nejprve si přečti jeho připojení a pak navrhni změnu, kterou schválím.',
    pausedCodeChanged:
      'Někdo provedl změnu v položce {thing} po připojení {connection} uživatelem {name}. Pro všechny je pozastavena, dokud {name} připojení v jejím chatu nepotvrdí.',
    pausedCodeChangedColleague:
      'Někdo provedl změnu v položce {thing} po potvrzení jejího připojení k {connection}. Pro všechny je pozastavena, dokud se připojení v jejím chatu znovu nepotvrdí.',
    pausedCodeChangedYou:
      'Někdo provedl změnu v položce {thing} po vašem připojení {connection}. Pro všechny je pozastavena, dokud připojení v jejím chatu nepotvrdíte.',
    fixInChatConfirmBody: 'Požádejte Chat, aby to potvrdil s vaším přístupem.',
  },
  da: {
    eyebrowShare: 'Del',
    eyebrowShared: 'Delt med dig',
    close: 'Luk',
    share: 'Del',
    shared: 'Delt',
    shareWith: 'Del: {audience}',
    whoTitle: 'Hvem kan åbne det',
    onlyYou: 'Kun dig',
    onlyYouBody: 'Ingen andre kan åbne det',
    everyone: 'Alle i {company}',
    everyoneBody: 'Alle i virksomheden kan åbne det',
    specificPeople: 'Udvalgte personer',
    specificPeopleBody: 'Vælg, hvem der kan åbne det',
    companyNeedsEdit: 'Kun personer, der kan redigere det, kan dele det med alle.',
    privateNeedsOwner: 'Kun ejeren eller en administrator kan gøre det privat igen.',
    skillCompany: 'En færdighed bliver tilgængelig for alle, når den udgives.',
    peopleCount: 'one:person|other:personer',
    addPeople: 'Tilføj personer efter navn eller e-mail',
    noMatches: 'Ingen kolleger matcher det.',
    noColleagues: 'Ingen andre kolleger er tilmeldt endnu.',
    loadingPeople: 'Indlæser kolleger…',
    peopleFailed: 'Vi kunne ikke indlæse dine kolleger. Luk, og prøv igen.',
    owner: 'Ejer',
    you: '{name} (dig)',
    canUse: 'Kan bruge',
    canUseBody: 'Åbne det og bruge den aktuelle version.',
    canEdit: 'Kan redigere',
    canEditBody: 'Ændre det og det, det kører på, via dets chat.',
    canView: 'Kan se',
    canViewBody: 'Læse det og følge nye svar.',
    canEditFile: 'Ændre det.',
    removeAccess: 'Fjern adgang',
    accessFor: 'Adgang for {name}',
    everyoneRowBody: 'Alle i virksomheden kan finde og åbne det',
    yourAccess: 'Din adgang',
    sharedBy: '{name} delte det med dig',
    sharedWithYou: 'Det blev delt med dig',
    ownerDecides: '{name} ejer det og bestemmer, hvem der kan åbne det.',
    runsOn: 'Kører på',
    runsOnHelp:
      'Disse forbindelser tilhører {thing}. Personer, der åbner det, kan se, hvad det viser, men får aldrig adgang til kontiene.',
    runsOnViewerHelp:
      'Du bruger disse forbindelser gennem {thing}. Du får ikke adgang til kontiene, og det bruger aldrig dine.',
    runsOnEmpty: 'Det bruger endnu ingen forbindelser.',
    runsOnFailed: 'Vi kunne ikke indlæse, hvad det kører på.',
    yours: 'Dine',
    from: 'Fra {name}',
    fromColleague: 'Fra en kollega',
    reads: 'Læser {list}',
    readsNothing: 'Læser ikke noget endnu',
    canChange: 'kan foretage ændringer',
    paused: 'Sat på pause',
    pausedBody:
      '{name}s adgang er udløbet, så {thing} kan ikke læse {connection} for nogen lige nu. Det skifter ikke automatisk til en andens konto.',
    pausedByColleague:
      'Den adgang, det brugte, er udløbet, så {thing} kan ikke læse {connection} for nogen lige nu. Det skifter ikke automatisk til en andens konto.',
    fixInChat: 'Løs i chatten',
    fixInChatBody: 'Bed Chat om at bruge din egen adgang i stedet.',
    chatHint: 'Hvis du vil tilføje eller ændre en forbindelse, kan du spørge i dens chat.',
    changeWithChat: 'Skift med Chat',
    editInChat: 'Rediger i chatten',
    unnamedConnection: 'Unavngiven forbindelse',
    retry: 'Prøv igen',
    cancel: 'Annuller',
    save: 'Gem',
    done: 'Færdig',
    saving: 'Gemmer…',
    statusPrivate: 'Kun du kan åbne det.',
    statusCompany: 'Alle hos {company} kan åbne det.',
    statusPeople: 'Delt med {people}.',
    statusPeopleEmpty: 'Tilføj personer for at dele det.',
    statusPending: 'Intet ændres, før du gemmer.',
    theApp: 'appen',
    theAgent: 'agenten',
    theSkill: 'færdigheden',
    theResult: 'resultatet',
    theItem: 'det',
    untitled: 'Ikke navngivet',
    updated: 'Deling er opdateret.',
    agentNoOwner:
      'Ejeren har forladt virksomheden, så agenten forbliver delt. Slå den fra, eller arkivér den.',
    agentPersonal:
      'Dette valg gør agenten privat for dens ejer: den slås fra, ventende kørsler afsluttes, og næste kørsel starter et nyt resultat.',
    changesPrompt:
      'Hjælp mig med at ændre, hvad {kind} {id} ({name}) kører på. Læs først forbindelserne, og foreslå derefter ændringen, så jeg kan godkende den.',
    pausedCodeChanged:
      'Nogen har ændret {thing}, efter at {name} forbandt {connection}. Det er sat på pause for alle, indtil {name} bekræfter forbindelsen i dens chat.',
    pausedCodeChangedColleague:
      'Nogen har ændret {thing}, efter at forbindelsen til {connection} blev bekræftet. Det er sat på pause for alle, indtil forbindelsen bekræftes igen i dens chat.',
    pausedCodeChangedYou:
      'Nogen har ændret {thing}, efter at du forbandt {connection}. Det er sat på pause for alle, indtil du bekræfter forbindelsen i dens chat.',
    fixInChatConfirmBody: 'Bed Chat om at bekræfte det med din adgang.',
  },
  nl: {
    eyebrowShare: 'Delen',
    eyebrowShared: 'Met jou gedeeld',
    close: 'Sluiten',
    share: 'Delen',
    shared: 'Gedeeld',
    shareWith: 'Delen: {audience}',
    whoTitle: 'Wie kan het openen',
    onlyYou: 'Alleen jij',
    onlyYouBody: 'Niemand anders kan het openen',
    everyone: 'Iedereen bij {company}',
    everyoneBody: 'Iedereen in het bedrijf kan het openen',
    specificPeople: 'Specifieke personen',
    specificPeopleBody: 'Kies wie het kan openen',
    companyNeedsEdit: 'Alleen mensen die het kunnen bewerken, kunnen het met iedereen delen.',
    privateNeedsOwner: 'Alleen de eigenaar of een beheerder kan het weer privé maken.',
    skillCompany: 'Een Skill bereikt iedereen zodra deze is gepubliceerd.',
    peopleCount: 'one:persoon|other:personen',
    addPeople: 'Voeg mensen toe op naam of e-mailadres',
    noMatches: 'Geen collega komt daarmee overeen.',
    noColleagues: "Er zijn nog geen andere collega's lid geworden.",
    loadingPeople: "Collega's worden geladen…",
    peopleFailed: "We konden je collega's niet laden. Sluit dit venster en probeer het opnieuw.",
    owner: 'Eigenaar',
    you: '{name} (jij)',
    canUse: 'Kan gebruiken',
    canUseBody: 'De huidige versie openen en gebruiken.',
    canEdit: 'Kan bewerken',
    canEditBody: 'Het wijzigen en aanpassen wat het uitvoert via de chat.',
    canView: 'Kan bekijken',
    canViewBody: 'Het lezen en nieuwe antwoorden volgen.',
    canEditFile: 'Wijzigen.',
    removeAccess: 'Toegang verwijderen',
    accessFor: 'Toegang voor {name}',
    everyoneRowBody: 'Iedereen in het bedrijf kan het vinden en openen',
    yourAccess: 'Jouw toegang',
    sharedBy: '{name} heeft het met je gedeeld',
    sharedWithYou: 'Het is met je gedeeld',
    ownerDecides: '{name} is de eigenaar en bepaalt wie het kan openen.',
    runsOn: 'Draait op',
    runsOnHelp:
      'Deze verbindingen zijn van {thing}. Mensen die het openen, zien wat het toont, maar krijgen nooit toegang tot de accounts.',
    runsOnViewerHelp:
      'Je gebruikt deze verbindingen via {thing}. Je krijgt geen toegang tot de accounts en het gebruikt die van jou nooit.',
    runsOnEmpty: 'Het gebruikt nog geen verbindingen.',
    runsOnFailed: 'We konden niet laden waarop het draait.',
    yours: 'Van jou',
    from: 'Van {name}',
    fromColleague: 'Van een collega',
    reads: 'Leest {list}',
    readsNothing: 'Leest nog niets',
    canChange: 'kan wijzigingen aanbrengen',
    paused: 'Gepauzeerd',
    pausedBody:
      'De toegang van {name} is beëindigd, dus {thing} kan {connection} momenteel voor niemand lezen. Het schakelt niet vanzelf over naar het account van iemand anders.',
    pausedByColleague:
      'De gebruikte toegang is beëindigd, dus {thing} kan {connection} momenteel voor niemand lezen. Het schakelt niet vanzelf over naar het account van iemand anders.',
    fixInChat: 'In de chat oplossen',
    fixInChatBody: 'Vraag Chat om in plaats daarvan jouw eigen toegang te gebruiken.',
    chatHint: 'Vraag in de chat om een verbinding toe te voegen of te wijzigen.',
    changeWithChat: 'Wijzigen met Chat',
    editInChat: 'Bewerken in de chat',
    unnamedConnection: 'Naamloze verbinding',
    retry: 'Opnieuw proberen',
    cancel: 'Annuleren',
    save: 'Opslaan',
    done: 'Klaar',
    saving: 'Opslaan…',
    statusPrivate: 'Alleen jij kunt het openen.',
    statusCompany: 'Iedereen bij {company} kan het openen.',
    statusPeople: 'Gedeeld met {people}.',
    statusPeopleEmpty: 'Voeg mensen toe om het te delen.',
    statusPending: 'Er verandert niets totdat je opslaat.',
    theApp: 'de app',
    theAgent: 'de agent',
    theSkill: 'de vaardigheid',
    theResult: 'het resultaat',
    theItem: 'dit',
    untitled: 'Naamloos',
    updated: 'Delen bijgewerkt.',
    agentNoOwner:
      'De eigenaar is vertrokken, dus de agent blijft gedeeld. Schakel hem uit of archiveer hem.',
    agentPersonal:
      'Deze keuze maakt de agent privé voor de eigenaar: hij wordt uitgeschakeld, wachtende uitvoeringen eindigen en de volgende uitvoering begint een nieuw resultaat.',
    changesPrompt:
      'Help me wijzigen waarop {kind} {id} ({name}) draait. Lees eerst de verbindingen en stel daarna de wijziging voor ter goedkeuring.',
    pausedCodeChanged:
      'Iemand heeft {thing} gewijzigd nadat {name} {connection} had verbonden. Het is voor iedereen gepauzeerd totdat {name} de verbinding in de chat bevestigt.',
    pausedCodeChangedColleague:
      'Iemand heeft {thing} gewijzigd nadat de verbinding met {connection} was bevestigd. Het is voor iedereen gepauzeerd totdat de verbinding opnieuw in de chat wordt bevestigd.',
    pausedCodeChangedYou:
      'Iemand heeft {thing} gewijzigd nadat je {connection} had verbonden. Het is voor iedereen gepauzeerd totdat je de verbinding in de chat bevestigt.',
    fixInChatConfirmBody: 'Vraag Chat om het te bevestigen met jouw toegang.',
  },
  en: {
    eyebrowShare: 'Share',
    eyebrowShared: 'Shared with you',
    close: 'Close',
    share: 'Share',
    shared: 'Shared',
    shareWith: 'Share: {audience}',
    whoTitle: 'Who can open it',
    onlyYou: 'Only you',
    onlyYouBody: 'Nobody else can open it',
    everyone: 'Everyone at {company}',
    everyoneBody: 'Anyone in the company can open it',
    specificPeople: 'Specific people',
    specificPeopleBody: 'Choose who can open it',
    companyNeedsEdit: 'Only people who can edit it can share it with everyone.',
    privateNeedsOwner: 'Only its owner or an admin can make it private again.',
    skillCompany: 'A Skill reaches everyone when it is published.',
    peopleCount: 'one:person|other:people',
    addPeople: 'Add people by name or email',
    noMatches: 'No colleague matches that.',
    noColleagues: 'No other colleagues have joined yet.',
    loadingPeople: 'Loading colleagues…',
    peopleFailed: 'We couldn’t load your colleagues. Close and try again.',
    owner: 'Owner',
    you: '{name} (you)',
    canUse: 'Can use',
    canUseBody: 'Open it and use the current version.',
    canEdit: 'Can edit',
    canEditBody: 'Change it and what it runs on, through its chat.',
    canView: 'Can view',
    canViewBody: 'Read it and follow new answers.',
    canEditFile: 'Change it.',
    removeAccess: 'Remove access',
    accessFor: 'Access for {name}',
    everyoneRowBody: 'Anyone in the company can find and open it',
    yourAccess: 'Your access',
    sharedBy: '{name} shared it with you',
    sharedWithYou: 'It was shared with you',
    ownerDecides: '{name} owns it and decides who can open it.',
    runsOn: 'Runs on',
    runsOnHelp:
      'These connections belong to {thing}. People who open it see what it shows, but never get the accounts.',
    runsOnViewerHelp:
      'You use these connections through {thing}. You don’t get the accounts, and it never uses yours.',
    runsOnEmpty: 'It doesn’t use any connections yet.',
    runsOnFailed: 'We couldn’t load what it runs on.',
    yours: 'Yours',
    from: 'From {name}',
    fromColleague: 'From a colleague',
    reads: 'Reads {list}',
    readsNothing: 'Reads nothing yet',
    canChange: 'can make changes',
    paused: 'Paused',
    pausedBody:
      '{name}’s access ended, so {thing} can’t read {connection} for anyone right now. It won’t switch to someone else’s account on its own.',
    pausedByColleague:
      'The access it used has ended, so {thing} can’t read {connection} for anyone right now. It won’t switch to someone else’s account on its own.',
    fixInChat: 'Fix in chat',
    fixInChatBody: 'Ask Chat to use your own access instead.',
    chatHint: 'To add or change a connection, ask in its chat.',
    changeWithChat: 'Change with Chat',
    editInChat: 'Edit in chat',
    unnamedConnection: 'Unnamed connection',
    retry: 'Try again',
    cancel: 'Cancel',
    save: 'Save',
    done: 'Done',
    saving: 'Saving…',
    statusPrivate: 'Only you can open it.',
    statusCompany: 'Everyone at {company} can open it.',
    statusPeople: 'Shared with {people}.',
    statusPeopleEmpty: 'Add people to share it.',
    statusPending: 'Nothing changes until you save.',
    theApp: 'the App',
    theAgent: 'the Agent',
    theSkill: 'the Skill',
    theResult: 'the result',
    theItem: 'it',
    untitled: 'Untitled',
    updated: 'Sharing updated.',
    agentNoOwner: 'Its owner left, so it stays shared; switch it off or archive it instead.',
    agentPersonal:
      'Only you makes it private to its owner: it switches off, waiting runs end, and its next run starts a new Result.',
    changesPrompt:
      'Help me change what {kind} {id} ({name}) runs on. Read its connections first, then propose the change for me to approve.',
    pausedCodeChanged:
      'Someone changed {thing} after {name} connected {connection}. It’s paused for everyone until {name} confirms the connection in its chat.',
    pausedCodeChangedColleague:
      'Someone changed {thing} after its connection to {connection} was confirmed. It’s paused for everyone until the connection is confirmed again in its chat.',
    pausedCodeChangedYou:
      'Someone changed {thing} after you connected {connection}. It’s paused for everyone until you confirm the connection in its chat.',
    fixInChatConfirmBody: 'Ask Chat to confirm it with your access.',
  },
  et: {
    eyebrowShare: 'Jaga',
    eyebrowShared: 'Sinuga jagatud',
    close: 'Sulge',
    share: 'Jaga',
    shared: 'Jagatud',
    shareWith: 'Jaga: {audience}',
    whoTitle: 'Kes saab avada',
    onlyYou: 'Ainult sina',
    onlyYouBody: 'Keegi teine ei saa seda avada',
    everyone: 'Kõik ettevõttes {company}',
    everyoneBody: 'Kõik ettevõtte liikmed saavad seda avada',
    specificPeople: 'Kindlad inimesed',
    specificPeopleBody: 'Vali, kes seda avada saavad',
    companyNeedsEdit: 'Ainult inimesed, kes saavad seda muuta, võivad seda kõigiga jagada.',
    privateNeedsOwner: 'Ainult omanik või administraator saab selle uuesti privaatseks muuta.',
    skillCompany: 'Oskus jõuab kõigini, kui see avaldatakse.',
    peopleCount: 'one:inimene|other:inimest',
    addPeople: 'Lisa inimesi nime või e-posti aadressi järgi',
    noMatches: 'Sobivat kolleegi ei leitud.',
    noColleagues: 'Ükski teine kolleeg pole veel liitunud.',
    loadingPeople: 'Kolleegide laadimine…',
    peopleFailed: 'Kolleegide laadimine ebaõnnestus. Sulge ja proovi uuesti.',
    owner: 'Omanik',
    you: '{name} (sina)',
    canUse: 'Saab kasutada',
    canUseBody: 'Ava see ja kasuta praegust versiooni.',
    canEdit: 'Saab muuta',
    canEditBody: 'Muuda seda ja selle vestluse kaudu kasutatavaid ressursse.',
    canView: 'Saab vaadata',
    canViewBody: 'Loe seda ja jälgi uusi vastuseid.',
    canEditFile: 'Muuda seda.',
    removeAccess: 'Eemalda juurdepääs',
    accessFor: 'Kasutaja {name} juurdepääs',
    everyoneRowBody: 'Kõik ettevõtte liikmed saavad seda leida ja avada',
    yourAccess: 'Sinu juurdepääs',
    sharedBy: '{name} jagas seda sinuga',
    sharedWithYou: 'Seda jagati sinuga',
    ownerDecides: '{name} on omanik ja otsustab, kes seda avada saab.',
    runsOn: 'Kasutab',
    runsOnHelp:
      'Need ühendused kuuluvad üksusele {thing}. Selle avajad näevad, mida see kuvab, kuid ei saa kunagi kontodele juurdepääsu.',
    runsOnViewerHelp:
      'Kasutad neid ühendusi üksuse {thing} kaudu. Sa ei saa kontodele juurdepääsu ja see ei kasuta kunagi sinu kontosid.',
    runsOnEmpty: 'See ei kasuta veel ühtegi ühendust.',
    runsOnFailed: 'Kasutatavate ühenduste laadimine ebaõnnestus.',
    yours: 'Sinu oma',
    from: 'Kasutajalt {name}',
    fromColleague: 'Kolleegilt',
    reads: 'Loeb: {list}',
    readsNothing: 'Ei loe veel midagi',
    canChange: 'saab muudatusi teha',
    paused: 'Peatatud',
    pausedBody:
      'Kasutaja {name} juurdepääs lõppes, mistõttu {thing} ei saa praegu kellegi jaoks ühendusest {connection} lugeda. See ei hakka iseenesest kellegi teise kontot kasutama.',
    pausedByColleague:
      'Selle kasutatud juurdepääs lõppes, mistõttu {thing} ei saa praegu kellegi jaoks ühendusest {connection} lugeda. See ei hakka iseenesest kellegi teise kontot kasutama.',
    fixInChat: 'Paranda vestluses',
    fixInChatBody: 'Palu Chatil kasutada selle asemel sinu juurdepääsu.',
    chatHint: 'Ühenduse lisamiseks või muutmiseks küsi seda selle vestluses.',
    changeWithChat: 'Muuda Chatiga',
    editInChat: 'Muuda vestluses',
    unnamedConnection: 'Nimetu ühendus',
    retry: 'Proovi uuesti',
    cancel: 'Tühista',
    save: 'Salvesta',
    done: 'Valmis',
    saving: 'Salvestamine…',
    statusPrivate: 'Ainult sina saad seda avada.',
    statusCompany: 'Kõik ettevõtte {company} liikmed saavad seda avada.',
    statusPeople: 'Jagatud kasutajatega {people}.',
    statusPeopleEmpty: 'Jaga seda, lisades inimesi.',
    statusPending: 'Miski ei muutu enne salvestamist.',
    theApp: 'rakendus',
    theAgent: 'agent',
    theSkill: 'oskus',
    theResult: 'tulemus',
    theItem: 'see',
    untitled: 'Pealkirjata',
    updated: 'Jagamine on uuendatud.',
    agentNoOwner: 'Omanik lahkus, seega jääb agent jagatuks. Lülita see välja või arhiveeri.',
    agentPersonal:
      'See valik muudab agendi omaniku jaoks privaatseks: agent lülitub välja, ootel käivitused lõpevad ja järgmine käivitus alustab uut tulemust.',
    changesPrompt:
      'Aita mul muuta, millel {kind} {id} ({name}) töötab. Loe esmalt selle ühendused ja paku seejärel muudatus heakskiitmiseks.',
    pausedCodeChanged:
      'Keegi muutis kirjet {thing} pärast seda, kui {name} ühendas ühenduse {connection}. See on kõigi jaoks peatatud, kuni {name} kinnitab ühenduse selle vestluses.',
    pausedCodeChangedColleague:
      'Keegi muutis kirjet {thing} pärast seda, kui selle ühendus {connection} oli kinnitatud. See on kõigi jaoks peatatud, kuni ühendus selle vestluses uuesti kinnitatakse.',
    pausedCodeChangedYou:
      'Keegi muutis kirjet {thing} pärast seda, kui ühendasid ühenduse {connection}. See on kõigi jaoks peatatud, kuni kinnitad ühenduse selle vestluses.',
    fixInChatConfirmBody: 'Palu Chatil see sinu juurdepääsuga kinnitada.',
  },
  fi: {
    eyebrowShare: 'Jaa',
    eyebrowShared: 'Jaettu kanssasi',
    close: 'Sulje',
    share: 'Jaa',
    shared: 'Jaettu',
    shareWith: 'Jaa: {audience}',
    whoTitle: 'Kuka voi avata sen',
    onlyYou: 'Vain sinä',
    onlyYouBody: 'Kukaan muu ei voi avata sitä',
    everyone: 'Kaikki organisaatiossa {company}',
    everyoneBody: 'Kuka tahansa yrityksessä voi avata sen',
    specificPeople: 'Valitut henkilöt',
    specificPeopleBody: 'Valitse, ketkä voivat avata sen',
    companyNeedsEdit: 'Vain henkilöt, joilla on muokkausoikeus, voivat jakaa sen kaikille.',
    privateNeedsOwner: 'Vain omistaja tai ylläpitäjä voi muuttaa sen jälleen yksityiseksi.',
    skillCompany: 'Taito tulee kaikkien saataville, kun se julkaistaan.',
    peopleCount: 'one:henkilö|other:henkilöä',
    addPeople: 'Lisää henkilöitä nimellä tai sähköpostiosoitteella',
    noMatches: 'Yksikään kollega ei vastaa hakua.',
    noColleagues: 'Muita kollegoita ei ole vielä liittynyt.',
    loadingPeople: 'Ladataan kollegoita…',
    peopleFailed: 'Kollegoidesi lataaminen epäonnistui. Sulje ja yritä uudelleen.',
    owner: 'Omistaja',
    you: '{name} (sinä)',
    canUse: 'Voi käyttää',
    canUseBody: 'Avaa sen ja käyttää nykyistä versiota.',
    canEdit: 'Voi muokata',
    canEditBody: 'Muokkaa sitä ja sen käyttämiä yhteyksiä chatin kautta.',
    canView: 'Voi tarkastella',
    canViewBody: 'Lukee sitä ja seuraa uusia vastauksia.',
    canEditFile: 'Muuta sitä.',
    removeAccess: 'Poista käyttöoikeus',
    accessFor: 'Henkilön {name} käyttöoikeus',
    everyoneRowBody: 'Kuka tahansa yrityksessä voi löytää ja avata sen',
    yourAccess: 'Käyttöoikeutesi',
    sharedBy: '{name} jakoi sen kanssasi',
    sharedWithYou: 'Se jaettiin kanssasi',
    ownerDecides: '{name} omistaa sen ja päättää, kuka voi avata sen.',
    runsOn: 'Käyttää yhteyksiä',
    runsOnHelp:
      'Nämä yhteydet kuuluvat kohteelle {thing}. Sen avaavat henkilöt näkevät, mitä se näyttää, mutta eivät koskaan saa käyttöönsä tilejä.',
    runsOnViewerHelp:
      'Käytät näitä yhteyksiä kohteen {thing} kautta. Et saa käyttöön tilejä, eikä se käytä tiliäsi.',
    runsOnEmpty: 'Se ei vielä käytä yhteyksiä.',
    runsOnFailed: 'Sen käyttämien yhteyksien lataaminen epäonnistui.',
    yours: 'Omasi',
    from: 'Henkilöltä {name}',
    fromColleague: 'Kollegalta',
    reads: 'Lukee kohdetta {list}',
    readsNothing: 'Ei lue vielä mitään',
    canChange: 'voi tehdä muutoksia',
    paused: 'Keskeytetty',
    pausedBody:
      'Henkilön {name} käyttöoikeus päättyi, joten {thing} ei voi tällä hetkellä lukea kohdetta {connection} kenenkään puolesta. Se ei vaihda automaattisesti jonkun toisen tiliin.',
    pausedByColleague:
      'Sen käyttämä käyttöoikeus päättyi, joten {thing} ei voi tällä hetkellä lukea kohdetta {connection} kenenkään puolesta. Se ei vaihda automaattisesti jonkun toisen tiliin.',
    fixInChat: 'Korjaa chatissa',
    fixInChatBody: 'Pyydä Chatia käyttämään sen sijaan omaa käyttöoikeuttasi.',
    chatHint: 'Voit lisätä tai muuttaa yhteyttä pyytämällä sitä sen chatissa.',
    changeWithChat: 'Muuta Chatissa',
    editInChat: 'Muokkaa chatissa',
    unnamedConnection: 'Nimeämätön yhteys',
    retry: 'Yritä uudelleen',
    cancel: 'Peruuta',
    save: 'Tallenna',
    done: 'Valmis',
    saving: 'Tallennetaan…',
    statusPrivate: 'Vain sinä voit avata sen.',
    statusCompany: 'Kaikki yrityksen {company} jäsenet voivat avata sen.',
    statusPeople: 'Jaettu henkilöille: {people}.',
    statusPeopleEmpty: 'Lisää henkilöitä jakaaksesi sen.',
    statusPending: 'Mikään ei muutu, ennen kuin tallennat.',
    theApp: 'sovellus',
    theAgent: 'agentti',
    theSkill: 'taito',
    theResult: 'tulos',
    theItem: 'se',
    untitled: 'Nimetön',
    updated: 'Jakaminen päivitetty.',
    agentNoOwner:
      'Omistaja on lähtenyt, joten agentti pysyy jaettuna. Poista se käytöstä tai arkistoi se.',
    agentPersonal:
      'Tämä valinta tekee agentista omistajansa yksityisen agentin: se poistuu käytöstä, odottavat ajot päättyvät ja seuraava ajo aloittaa uuden tuloksen.',
    changesPrompt:
      'Auta minua muuttamaan, millä {kind} {id} ({name}) toimii. Lue ensin sen yhteydet ja ehdota sitten muutosta hyväksyttäväkseni.',
    pausedCodeChanged:
      'Joku muutti kohdetta {thing} sen jälkeen, kun {name} yhdisti yhteyden {connection}. Se on keskeytetty kaikilta, kunnes {name} vahvistaa yhteyden sen keskustelussa.',
    pausedCodeChangedColleague:
      'Joku muutti kohdetta {thing} sen jälkeen, kun sen yhteys {connection} vahvistettiin. Se on keskeytetty kaikilta, kunnes yhteys vahvistetaan uudelleen sen keskustelussa.',
    pausedCodeChangedYou:
      'Joku muutti kohdetta {thing} sen jälkeen, kun yhdistit yhteyden {connection}. Se on keskeytetty kaikilta, kunnes vahvistat yhteyden sen keskustelussa.',
    fixInChatConfirmBody: 'Pyydä Chatia vahvistamaan se käyttöoikeudellasi.',
  },
  fr: {
    eyebrowShare: 'Partager',
    eyebrowShared: 'Partagé avec vous',
    close: 'Fermer',
    share: 'Partager',
    shared: 'Partagée',
    shareWith: 'Partager: {audience}',
    whoTitle: 'Qui peut l’ouvrir',
    onlyYou: 'Vous uniquement',
    onlyYouBody: 'Personne d’autre ne peut l’ouvrir',
    everyone: 'Tout le monde chez {company}',
    everyoneBody: 'Tout le monde dans l’entreprise peut l’ouvrir',
    specificPeople: 'Personnes précises',
    specificPeopleBody: 'Choisissez qui peut l’ouvrir',
    companyNeedsEdit:
      'Seules les personnes qui peuvent le modifier peuvent le partager avec tout le monde.',
    privateNeedsOwner: 'Seul son propriétaire ou un administrateur peut le rendre privé à nouveau.',
    skillCompany: 'Une compétence est accessible à tout le monde lorsqu’elle est publiée.',
    peopleCount: 'one:personne|other:personnes',
    addPeople: 'Ajouter des personnes par nom ou par e-mail',
    noMatches: 'Aucun collègue ne correspond.',
    noColleagues: 'Aucun autre collègue n’a encore rejoint l’entreprise.',
    loadingPeople: 'Chargement des collègues…',
    peopleFailed: 'Nous n’avons pas pu charger vos collègues. Fermez, puis réessayez.',
    owner: 'Propriétaire',
    you: '{name} (vous)',
    canUse: 'Peut utiliser',
    canUseBody: 'L’ouvrir et utiliser la version actuelle.',
    canEdit: 'Peut modifier',
    canEditBody: 'Le modifier ainsi que ce sur quoi il s’exécute, via son chat.',
    canView: 'Peut consulter',
    canViewBody: 'Le lire et suivre les nouvelles réponses.',
    canEditFile: 'Le modifier.',
    removeAccess: 'Supprimer l’accès',
    accessFor: 'Accès de {name}',
    everyoneRowBody: 'Tout le monde dans l’entreprise peut le trouver et l’ouvrir',
    yourAccess: 'Votre accès',
    sharedBy: '{name} vous l’a partagé',
    sharedWithYou: 'Il a été partagé avec vous',
    ownerDecides: '{name} en est le propriétaire et décide qui peut l’ouvrir.',
    runsOn: 'Fonctionne avec',
    runsOnHelp:
      'Ces connexions appartiennent à {thing}. Les personnes qui l’ouvrent voient ce qu’il affiche, mais n’obtiennent jamais les comptes.',
    runsOnViewerHelp:
      'Vous utilisez ces connexions via {thing}. Vous n’obtenez pas les comptes, et il n’utilise jamais les vôtres.',
    runsOnEmpty: 'Il n’utilise encore aucune connexion.',
    runsOnFailed: 'Nous n’avons pas pu charger les éléments avec lesquels il fonctionne.',
    yours: 'Les vôtres',
    from: 'De la part de {name}',
    fromColleague: 'D’un collègue',
    reads: 'Lit {list}',
    readsNothing: 'Ne lit encore rien',
    canChange: 'peut apporter des modifications',
    paused: 'En pause',
    pausedBody:
      'L’accès de {name} a pris fin, donc {thing} ne peut plus lire {connection} pour personne. Il ne basculera pas automatiquement vers le compte de quelqu’un d’autre.',
    pausedByColleague:
      'L’accès utilisé a pris fin, donc {thing} ne peut plus lire {connection} pour personne. Il ne basculera pas automatiquement vers le compte de quelqu’un d’autre.',
    fixInChat: 'Corriger dans le chat',
    fixInChatBody: 'Demandez à Chat d’utiliser votre propre accès à la place.',
    chatHint: 'Pour ajouter ou modifier une connexion, demandez-le dans son chat.',
    changeWithChat: 'Modifier avec Chat',
    editInChat: 'Modifier dans le chat',
    unnamedConnection: 'Connexion sans nom',
    retry: 'Réessayer',
    cancel: 'Annuler',
    save: 'Enregistrer',
    done: 'Terminé',
    saving: 'Enregistrement…',
    statusPrivate: 'Vous seul pouvez l’ouvrir.',
    statusCompany: 'Tout le monde chez {company} peut l’ouvrir.',
    statusPeople: 'Partagé avec {people}.',
    statusPeopleEmpty: 'Ajoutez des personnes pour le partager.',
    statusPending: 'Rien ne change tant que vous n’avez pas enregistré.',
    theApp: 'l’application',
    theAgent: 'l’agent',
    theSkill: 'la compétence',
    theResult: 'le résultat',
    theItem: 'l’élément',
    untitled: 'Sans titre',
    updated: 'Partage mis à jour.',
    agentNoOwner:
      'Son propriétaire est parti ; l’agent reste partagé. Désactivez-le ou archivez-le.',
    agentPersonal:
      'Ce choix rend l’agent privé pour son propriétaire : il est désactivé, les exécutions en attente prennent fin et la prochaine crée un nouveau résultat.',
    changesPrompt:
      'Aidez-moi à modifier ce sur quoi s’exécute {kind} {id} ({name}). Lisez d’abord ses connexions, puis proposez-moi la modification à approuver.',
    pausedCodeChanged:
      'Quelqu’un a modifié {thing} après que {name} a connecté {connection}. L’élément est en pause pour tout le monde jusqu’à ce que {name} confirme la connexion dans son chat.',
    pausedCodeChangedColleague:
      'Quelqu’un a modifié {thing} après la confirmation de sa connexion à {connection}. L’élément est en pause pour tout le monde jusqu’à une nouvelle confirmation de la connexion dans son chat.',
    pausedCodeChangedYou:
      'Quelqu’un a modifié {thing} après que vous avez connecté {connection}. L’élément est en pause pour tout le monde jusqu’à ce que vous confirmiez la connexion dans son chat.',
    fixInChatConfirmBody: 'Demandez au chat de le confirmer avec votre accès.',
  },
  de: {
    eyebrowShare: 'Teilen',
    eyebrowShared: 'Mit Ihnen geteilt',
    close: 'Schließen',
    share: 'Teilen',
    shared: 'Geteilt',
    shareWith: 'Teilen: {audience}',
    whoTitle: 'Wer kann es öffnen?',
    onlyYou: 'Nur Sie',
    onlyYouBody: 'Niemand sonst kann es öffnen',
    everyone: 'Alle bei {company}',
    everyoneBody: 'Jede Person im Unternehmen kann es öffnen',
    specificPeople: 'Bestimmte Personen',
    specificPeopleBody: 'Wählen Sie aus, wer es öffnen kann',
    companyNeedsEdit: 'Nur Personen mit Bearbeitungszugriff können es mit allen teilen.',
    privateNeedsOwner: 'Nur der Eigentümer oder ein Administrator kann es wieder privat machen.',
    skillCompany: 'Ein Skill ist für alle verfügbar, sobald er veröffentlicht wurde.',
    peopleCount: 'one:Person|other:Personen',
    addPeople: 'Personen nach Name oder E-Mail-Adresse hinzufügen',
    noMatches: 'Keine passende Kollegin oder kein passender Kollege gefunden.',
    noColleagues: 'Bisher ist noch keine weitere Kollegin und kein weiterer Kollege beigetreten.',
    loadingPeople: 'Kolleginnen und Kollegen werden geladen…',
    peopleFailed:
      'Ihre Kolleginnen und Kollegen konnten nicht geladen werden. Schließen Sie das Fenster und versuchen Sie es erneut.',
    owner: 'Eigentümer',
    you: '{name} (Sie)',
    canUse: 'Kann es verwenden',
    canUseBody: 'Es öffnen und die aktuelle Version verwenden.',
    canEdit: 'Kann es bearbeiten',
    canEditBody: 'Es und seine Ausführung über den zugehörigen Chat ändern.',
    canView: 'Kann es ansehen',
    canViewBody: 'Es lesen und neue Antworten verfolgen.',
    canEditFile: 'Kann es ändern.',
    removeAccess: 'Zugriff entfernen',
    accessFor: 'Zugriff für {name}',
    everyoneRowBody: 'Jede Person im Unternehmen kann es finden und öffnen',
    yourAccess: 'Ihr Zugriff',
    sharedBy: '{name} hat es mit Ihnen geteilt',
    sharedWithYou: 'Es wurde mit Ihnen geteilt',
    ownerDecides: '{name} ist der Eigentümer und entscheidet, wer es öffnen kann.',
    runsOn: 'Ausführung über',
    runsOnHelp:
      'Diese Verbindungen gehören zu {thing}. Personen, die es öffnen, sehen, was es anzeigt, erhalten aber niemals Zugriff auf die Konten.',
    runsOnViewerHelp:
      'Sie nutzen diese Verbindungen über {thing}. Sie erhalten keinen Zugriff auf die Konten, und Ihre Konten werden dabei nie verwendet.',
    runsOnEmpty: 'Bisher verwendet es keine Verbindungen.',
    runsOnFailed: 'Die Informationen zur Ausführung konnten nicht geladen werden.',
    yours: 'Ihre',
    from: 'Von {name}',
    fromColleague: 'Von einer Kollegin oder einem Kollegen',
    reads: 'Liest {list}',
    readsNothing: 'Liest bisher nichts',
    canChange: 'kann Änderungen vornehmen',
    paused: 'Pausiert',
    pausedBody:
      'Der Zugriff von {name} ist abgelaufen. Daher kann {thing} {connection} derzeit für niemanden lesen. Es wechselt nicht automatisch zum Konto einer anderen Person.',
    pausedByColleague:
      'Der verwendete Zugriff ist abgelaufen. Daher kann {thing} {connection} derzeit für niemanden lesen. Es wechselt nicht automatisch zum Konto einer anderen Person.',
    fixInChat: 'Im Chat beheben',
    fixInChatBody: 'Bitte Chat, stattdessen Ihren eigenen Zugriff zu verwenden.',
    chatHint: 'Um eine Verbindung hinzuzufügen oder zu ändern, fragen Sie im zugehörigen Chat.',
    changeWithChat: 'Mit Chat ändern',
    editInChat: 'Im Chat bearbeiten',
    unnamedConnection: 'Unbenannte Verbindung',
    retry: 'Erneut versuchen',
    cancel: 'Abbrechen',
    save: 'Speichern',
    done: 'Fertig',
    saving: 'Wird gespeichert…',
    statusPrivate: 'Nur Sie können es öffnen.',
    statusCompany: 'Alle bei {company} können es öffnen.',
    statusPeople: 'Mit {people} geteilt.',
    statusPeopleEmpty: 'Fügen Sie Personen hinzu, um es zu teilen.',
    statusPending: 'Die Änderungen werden erst beim Speichern übernommen.',
    theApp: 'die App',
    theAgent: 'der Agent',
    theSkill: 'der Skill',
    theResult: 'das Ergebnis',
    theItem: 'es',
    untitled: 'Ohne Titel',
    updated: 'Freigabe aktualisiert.',
    agentNoOwner:
      'Der Eigentümer hat das Unternehmen verlassen. Der Agent bleibt geteilt. Schalten Sie ihn aus oder archivieren Sie ihn.',
    agentPersonal:
      'Diese Auswahl macht den Agenten für seinen Eigentümer privat: Er wird ausgeschaltet, wartende Ausführungen enden und die nächste beginnt ein neues Ergebnis.',
    changesPrompt:
      'Helfen Sie mir zu ändern, worauf {kind} {id} ({name}) ausgeführt wird. Lesen Sie zuerst die Verbindungen und schlagen Sie mir dann die Änderung zur Genehmigung vor.',
    pausedCodeChanged:
      'Jemand hat {thing} geändert, nachdem {name} {connection} verbunden hat. Es ist für alle pausiert, bis {name} die Verbindung in seinem Chat bestätigt.',
    pausedCodeChangedColleague:
      'Jemand hat {thing} geändert, nachdem die Verbindung zu {connection} bestätigt wurde. Es ist für alle pausiert, bis die Verbindung im Chat erneut bestätigt wird.',
    pausedCodeChangedYou:
      'Jemand hat {thing} geändert, nachdem Sie {connection} verbunden haben. Es ist für alle pausiert, bis Sie die Verbindung in seinem Chat bestätigen.',
    fixInChatConfirmBody: 'Bitten Sie den Chat, es mit Ihrem Zugriff zu bestätigen.',
  },
  el: {
    eyebrowShare: 'Κοινή χρήση',
    eyebrowShared: 'Σε κοινή χρήση μαζί σας',
    close: 'Κλείσιμο',
    share: 'Κοινή χρήση',
    shared: 'Κοινόχρηστη',
    shareWith: 'Κοινή χρήση: {audience}',
    whoTitle: 'Ποιοι μπορούν να το ανοίξουν',
    onlyYou: 'Μόνο εσείς',
    onlyYouBody: 'Κανείς άλλος δεν μπορεί να το ανοίξει',
    everyone: 'Όλοι στην εταιρεία {company}',
    everyoneBody: 'Όλοι στην εταιρεία μπορούν να το ανοίξουν',
    specificPeople: 'Συγκεκριμένα άτομα',
    specificPeopleBody: 'Επιλέξτε ποιοι μπορούν να το ανοίξουν',
    companyNeedsEdit: 'Μόνο όσοι μπορούν να το επεξεργαστούν μπορούν να το μοιραστούν με όλους.',
    privateNeedsOwner: 'Μόνο ο κάτοχός του ή ένας διαχειριστής μπορεί να το κάνει ξανά ιδιωτικό.',
    skillCompany: 'Μια Δεξιότητα γίνεται διαθέσιμη σε όλους όταν δημοσιεύεται.',
    peopleCount: 'one:άτομο|other:άτομα',
    addPeople: 'Προσθήκη ατόμων με όνομα ή email',
    noMatches: 'Δεν υπάρχει συνάδελφος με αυτά τα στοιχεία.',
    noColleagues: 'Δεν έχει συμμετάσχει ακόμη κανένας άλλος συνάδελφος.',
    loadingPeople: 'Φόρτωση συναδέλφων…',
    peopleFailed: 'Δεν ήταν δυνατή η φόρτωση των συναδέλφων σας. Κλείστε και δοκιμάστε ξανά.',
    owner: 'Κάτοχος',
    you: '{name} (εσείς)',
    canUse: 'Μπορεί να χρησιμοποιεί',
    canUseBody: 'Να το ανοίγει και να χρησιμοποιεί την τρέχουσα έκδοση.',
    canEdit: 'Μπορεί να επεξεργάζεται',
    canEditBody: 'Να το αλλάζει και να αλλάζει σε τι εκτελείται, μέσω της συνομιλίας του.',
    canView: 'Μπορεί να βλέπει',
    canViewBody: 'Να το διαβάζει και να παρακολουθεί νέες απαντήσεις.',
    canEditFile: 'Να το αλλάζει.',
    removeAccess: 'Κατάργηση πρόσβασης',
    accessFor: 'Πρόσβαση για {name}',
    everyoneRowBody: 'Όλοι στην εταιρεία μπορούν να το βρουν και να το ανοίξουν',
    yourAccess: 'Η πρόσβασή σας',
    sharedBy: 'Ο/Η {name} το μοιράστηκε μαζί σας',
    sharedWithYou: 'Μοιράστηκε μαζί σας',
    ownerDecides: 'Ο/Η {name} είναι ο κάτοχός του και αποφασίζει ποιοι μπορούν να το ανοίξουν.',
    runsOn: 'Εκτελείται σε',
    runsOnHelp:
      'Αυτές οι συνδέσεις ανήκουν στο {thing}. Όσοι το ανοίγουν βλέπουν τι εμφανίζει, αλλά δεν αποκτούν ποτέ πρόσβαση στους λογαριασμούς.',
    runsOnViewerHelp:
      'Χρησιμοποιείτε αυτές τις συνδέσεις μέσω του {thing}. Δεν αποκτάτε πρόσβαση στους λογαριασμούς και δεν χρησιμοποιεί ποτέ τους δικούς σας.',
    runsOnEmpty: 'Δεν χρησιμοποιεί ακόμη καμία σύνδεση.',
    runsOnFailed: 'Δεν ήταν δυνατή η φόρτωση του περιβάλλοντος στο οποίο εκτελείται.',
    yours: 'Δικό σας',
    from: 'Από {name}',
    fromColleague: 'Από συνάδελφο',
    reads: 'Διαβάζει {list}',
    readsNothing: 'Δεν διαβάζει ακόμη τίποτα',
    canChange: 'μπορεί να κάνει αλλαγές',
    paused: 'Σε παύση',
    pausedBody:
      'Η πρόσβαση του/της {name} έληξε, επομένως το {thing} δεν μπορεί να διαβάσει το {connection} για κανέναν αυτήν τη στιγμή. Δεν θα μεταβεί αυτόματα στον λογαριασμό κάποιου άλλου.',
    pausedByColleague:
      'Η πρόσβαση που χρησιμοποιούσε έληξε, επομένως το {thing} δεν μπορεί να διαβάσει το {connection} για κανέναν αυτήν τη στιγμή. Δεν θα μεταβεί αυτόματα στον λογαριασμό κάποιου άλλου.',
    fixInChat: 'Διόρθωση στη συνομιλία',
    fixInChatBody: 'Ζητήστε από το Chat να χρησιμοποιήσει τη δική σας πρόσβαση.',
    chatHint: 'Για να προσθέσετε ή να αλλάξετε μια σύνδεση, ζητήστε το στη συνομιλία του.',
    changeWithChat: 'Αλλαγή μέσω Chat',
    editInChat: 'Επεξεργασία στη συνομιλία',
    unnamedConnection: 'Σύνδεση χωρίς όνομα',
    retry: 'Δοκιμή ξανά',
    cancel: 'Ακύρωση',
    save: 'Αποθήκευση',
    done: 'Τέλος',
    saving: 'Αποθήκευση…',
    statusPrivate: 'Μόνο εσείς μπορείτε να το ανοίξετε.',
    statusCompany: 'Όλοι στην {company} μπορούν να το ανοίξουν.',
    statusPeople: 'Σε κοινή χρήση με {people}.',
    statusPeopleEmpty: 'Προσθέστε άτομα για να το μοιραστείτε.',
    statusPending: 'Τίποτα δεν αλλάζει μέχρι να αποθηκεύσετε.',
    theApp: 'την Εφαρμογή',
    theAgent: 'τον Πράκτορα',
    theSkill: 'τη Δεξιότητα',
    theResult: 'το αποτέλεσμα',
    theItem: 'αυτό',
    untitled: 'Χωρίς τίτλο',
    updated: 'Η κοινή χρήση ενημερώθηκε.',
    agentNoOwner:
      'Ο κάτοχος αποχώρησε, οπότε ο πράκτορας παραμένει κοινόχρηστος. Απενεργοποιήστε ή αρχειοθετήστε τον.',
    agentPersonal:
      'Αυτή η επιλογή κάνει τον πράκτορα ιδιωτικό για τον κάτοχό του: απενεργοποιείται, οι εκτελέσεις σε αναμονή τερματίζονται και η επόμενη ξεκινά νέο αποτέλεσμα.',
    changesPrompt:
      'Βοήθησέ με να αλλάξω σε τι εκτελείται το {kind} {id} ({name}). Διάβασε πρώτα τις συνδέσεις του και μετά πρότεινέ μου την αλλαγή για έγκριση.',
    pausedCodeChanged:
      'Κάποιος άλλαξε το {thing} αφότου ο/η {name} συνέδεσε το {connection}. Είναι σε παύση για όλους μέχρι ο/η {name} να επιβεβαιώσει τη σύνδεση στη συνομιλία του.',
    pausedCodeChangedColleague:
      'Κάποιος άλλαξε το {thing} αφότου επιβεβαιώθηκε η σύνδεσή του με το {connection}. Είναι σε παύση για όλους μέχρι να επιβεβαιωθεί ξανά η σύνδεση στη συνομιλία του.',
    pausedCodeChangedYou:
      'Κάποιος άλλαξε το {thing} αφότου συνδέσατε το {connection}. Είναι σε παύση για όλους μέχρι να επιβεβαιώσετε τη σύνδεση στη συνομιλία του.',
    fixInChatConfirmBody: 'Ζητήστε από το Chat να το επιβεβαιώσει με την πρόσβασή σας.',
  },
  hu: {
    eyebrowShare: 'Megosztás',
    eyebrowShared: 'Megosztva veled',
    close: 'Bezárás',
    share: 'Megosztás',
    shared: 'Megosztott',
    shareWith: 'Megosztás: {audience}',
    whoTitle: 'Ki nyithatja meg',
    onlyYou: 'Csak Ön',
    onlyYouBody: 'Senki más nem nyithatja meg',
    everyone: 'Mindenki itt: {company}',
    everyoneBody: 'A vállalat bármely tagja megnyithatja',
    specificPeople: 'Adott személyek',
    specificPeopleBody: 'Válaszd ki, ki nyithatja meg',
    companyNeedsEdit: 'Csak azok oszthatják meg mindenkivel, akik szerkeszthetik.',
    privateNeedsOwner: 'Csak a tulajdonosa vagy egy adminisztrátor teheti újra priváttá.',
    skillCompany: 'A készség közzététel után mindenki számára elérhetővé válik.',
    peopleCount: 'one:fő|other:fő',
    addPeople: 'Személyek hozzáadása név vagy e-mail-cím alapján',
    noMatches: 'Nincs ilyen kolléga.',
    noColleagues: 'Még egyetlen másik kolléga sem csatlakozott.',
    loadingPeople: 'Kollégák betöltése…',
    peopleFailed: 'Nem sikerült betölteni a kollégáidat. Zárd be, majd próbáld újra.',
    owner: 'Tulajdonos',
    you: '{name} (te)',
    canUse: 'Használhatja',
    canUseBody: 'Megnyithatja és használhatja az aktuális verziót.',
    canEdit: 'Szerkesztheti',
    canEditBody: 'Módosíthatja a tartalmát és azt, amin fut, a csevegésén keresztül.',
    canView: 'Megtekintheti',
    canViewBody: 'Elolvashatja, és követheti az új válaszokat.',
    canEditFile: 'Módosíthatja.',
    removeAccess: 'Hozzáférés eltávolítása',
    accessFor: '{name} hozzáférése',
    everyoneRowBody: 'A vállalat bármely tagja megtalálhatja és megnyithatja',
    yourAccess: 'Saját hozzáférésed',
    sharedBy: '{name} megosztotta veled',
    sharedWithYou: 'Megosztották veled',
    ownerDecides: '{name} a tulajdonos, és ő dönti el, ki nyithatja meg.',
    runsOn: 'Ezeken fut',
    runsOnHelp:
      'Ezek a kapcsolatok {thing} tulajdonában vannak. A megnyitók látják, mit jelenít meg, de a fiókokhoz soha nem férnek hozzá.',
    runsOnViewerHelp:
      'Ezeket a kapcsolatokat {thing} közvetítésével használod. Nem kapod meg a fiókokat, és a rendszer soha nem használja a tieidet.',
    runsOnEmpty: 'Még nem használ kapcsolatokat.',
    runsOnFailed: 'Nem sikerült betölteni, min fut.',
    yours: 'Saját',
    from: '{name}től',
    fromColleague: 'Egy kollégától',
    reads: 'Ezeket olvassa: {list}',
    readsNothing: 'Még semmit sem olvas',
    canChange: 'módosíthatja',
    paused: 'Szüneteltetve',
    pausedBody:
      '{name} hozzáférése megszűnt, ezért {thing} jelenleg senki számára nem tudja olvasni ezt: {connection}. Magától nem vált át más fiókjára.',
    pausedByColleague:
      'A használt hozzáférés megszűnt, ezért {thing} jelenleg senki számára nem tudja olvasni ezt: {connection}. Magától nem vált át más fiókjára.',
    fixInChat: 'Javítás a csevegésben',
    fixInChatBody: 'Kérd meg a Chatet, hogy helyette a saját hozzáférésedet használja.',
    chatHint: 'Kapcsolat hozzáadásához vagy módosításához kérd meg erre a csevegésben.',
    changeWithChat: 'Módosítás a Chattel',
    editInChat: 'Szerkesztés a csevegésben',
    unnamedConnection: 'Névtelen kapcsolat',
    retry: 'Próbáld újra',
    cancel: 'Mégse',
    save: 'Mentés',
    done: 'Kész',
    saving: 'Mentés…',
    statusPrivate: 'Csak te nyithatod meg.',
    statusCompany: 'A(z) {company} vállalat minden tagja megnyithatja.',
    statusPeople: 'Megosztva ezekkel: {people}.',
    statusPeopleEmpty: 'Adj hozzá személyeket a megosztáshoz.',
    statusPending: 'A mentésig semmi sem változik.',
    theApp: 'az alkalmazás',
    theAgent: 'az ügynök',
    theSkill: 'a készség',
    theResult: 'az eredmény',
    theItem: 'az elem',
    untitled: 'Névtelen',
    updated: 'Megosztás frissítve.',
    agentNoOwner:
      'A tulajdonos távozott, ezért az ügynök megosztott marad. Kapcsolja ki vagy archiválja.',
    agentPersonal:
      'Ez a beállítás az ügynököt a tulajdonosa számára priváttá teszi: kikapcsol, a várakozó futások véget érnek, a következő futás pedig új eredményt indít.',
    changesPrompt:
      'Segíts megváltoztatni, hogy a(z) {kind} {id} ({name}) mit futtat. Először olvasd el a kapcsolatait, majd javasold a módosítást jóváhagyásra.',
    pausedCodeChanged:
      'Valaki módosította ezt: {thing}, miután {name} csatlakoztatta ezt: {connection}. Mindenki számára szünetel, amíg {name} meg nem erősíti a kapcsolatot a csevegésében.',
    pausedCodeChangedColleague:
      'Valaki módosította ezt: {thing}, miután megerősítették a kapcsolatát ezzel: {connection}. Mindenki számára szünetel, amíg a kapcsolatot újra meg nem erősítik a csevegésében.',
    pausedCodeChangedYou:
      'Valaki módosította ezt: {thing}, miután te csatlakoztattad ezt: {connection}. Mindenki számára szünetel, amíg meg nem erősíted a kapcsolatot a csevegésében.',
    fixInChatConfirmBody: 'Kérd meg a Chatet, hogy erősítse meg a te hozzáféréseddel.',
  },
  ga: {
    eyebrowShare: 'Comhroinn',
    eyebrowShared: 'Comhroinnte leat',
    close: 'Dún',
    share: 'Comhroinn',
    shared: 'Comhroinnte',
    shareWith: 'Comhroinn: {audience}',
    whoTitle: 'Cé atá in ann é a oscailt',
    onlyYou: 'Tusa amháin',
    onlyYouBody: 'Ní féidir le haon duine eile é a oscailt',
    everyone: 'Gach duine in {company}',
    everyoneBody: 'Is féidir le haon duine sa chuideachta é a oscailt',
    specificPeople: 'Daoine áirithe',
    specificPeopleBody: 'Roghnaigh cé atá in ann é a oscailt',
    companyNeedsEdit:
      'Ní féidir ach le daoine atá in ann é a chur in eagar é a roinnt le gach duine.',
    privateNeedsOwner: 'Ní féidir ach lena úinéir nó le riarthóir é a dhéanamh príobháideach arís.',
    skillCompany: 'Sroicheann Scil gach duine nuair a fhoilsítear í.',
    peopleCount: 'one:duine|two:dhuine|few:dhuine|many:nduine|other:duine',
    addPeople: 'Cuir daoine leis de réir ainm nó ríomhphoist',
    noMatches: 'Níor aimsíodh aon chomhghleacaí den sórt sin.',
    noColleagues: 'Níl aon chomhghleacaithe eile tar éis páirt a ghlacadh fós.',
    loadingPeople: 'Comhghleacaithe á lódáil…',
    peopleFailed:
      'Níorbh fhéidir linn do chomhghleacaithe a lódáil. Dún é agus bain triail eile as.',
    owner: 'Úinéir',
    you: '{name} (tú)',
    canUse: 'Is féidir é a úsáid',
    canUseBody: 'Oscail é agus bain úsáid as an leagan reatha.',
    canEdit: 'Is féidir é a chur in eagar',
    canEditBody: 'Athraigh é agus an méid a ritheann sé air, trína chomhrá.',
    canView: 'Is féidir é a fheiceáil',
    canViewBody: 'Léigh é agus lean freagraí nua.',
    canEditFile: 'Athraigh é.',
    removeAccess: 'Bain rochtain',
    accessFor: 'Rochtain do {name}',
    everyoneRowBody: 'Is féidir le haon duine sa chuideachta é a aimsiú agus a oscailt',
    yourAccess: 'Do rochtain',
    sharedBy: 'Roinn {name} é leat',
    sharedWithYou: 'Roinneadh leat é',
    ownerDecides: 'Is le {name} é agus socraíonn sé/sí cé atá in ann é a oscailt.',
    runsOn: 'Ritheann sé ar',
    runsOnHelp:
      'Is le {thing} na naisc seo. Feiceann daoine a osclaíonn é an méid a thaispeánann sé, ach ní fhaigheann siad rochtain ar na cuntais.',
    runsOnViewerHelp:
      'Úsáideann tú na naisc seo trí {thing}. Ní fhaigheann tú rochtain ar na cuntais, agus ní úsáideann sé do chuntais féin choíche.',
    runsOnEmpty: 'Ní úsáideann sé aon naisc fós.',
    runsOnFailed: 'Níorbh fhéidir linn an méid a ritheann sé air a lódáil.',
    yours: 'Is leatsa',
    from: 'Ó {name}',
    fromColleague: 'Ó chomhghleacaí',
    reads: 'Léann sé {list}',
    readsNothing: 'Ní léann sé tada fós',
    canChange: 'is féidir athruithe a dhéanamh',
    paused: 'Curtha ar sos',
    pausedBody:
      'Tháinig deireadh le rochtain {name}, mar sin ní féidir le {thing} {connection} a léamh do dhuine ar bith faoi láthair. Ní aistreoidh sé go cuntas duine eile as a stuaim féin.',
    pausedByColleague:
      'Tháinig deireadh leis an rochtain a d’úsáid sé, mar sin ní féidir le {thing} {connection} a léamh do dhuine ar bith faoi láthair. Ní aistreoidh sé go cuntas duine eile as a stuaim féin.',
    fixInChat: 'Deisigh sa chomhrá',
    fixInChatBody: 'Iarr ar Chat do rochtain féin a úsáid ina áit.',
    chatHint: 'Chun nasc a chur leis nó a athrú, fiafraigh sa chomhrá leis.',
    changeWithChat: 'Athraigh le Chat',
    editInChat: 'Cuir in eagar sa chomhrá',
    unnamedConnection: 'Nasc gan ainm',
    retry: 'Bain triail eile as',
    cancel: 'Cealaigh',
    save: 'Sábháil',
    done: 'Déanta',
    saving: 'Á shábháil…',
    statusPrivate: 'Ní féidir ach leatsa é a oscailt.',
    statusCompany: 'Is féidir le gach duine ag {company} é a oscailt.',
    statusPeople: 'Roinnte le {people}.',
    statusPeopleEmpty: 'Cuir daoine leis chun é a roinnt.',
    statusPending: 'Ní athróidh aon rud go dtí go sábhálfaidh tú é.',
    theApp: 'an Aip',
    theAgent: 'an Gníomhaire',
    theSkill: 'an Scil',
    theResult: 'an toradh',
    theItem: 'é',
    untitled: 'Gan teideal',
    updated: 'Nuashonraíodh an chomhroinnt.',
    agentNoOwner:
      'D’fhág an t-úinéir, mar sin fanann an gníomhaire comhroinnte. Múch é nó cuir sa chartlann é.',
    agentPersonal:
      'Déanann an rogha seo an gníomhaire príobháideach dá úinéir: múchtar é, críochnaítear na ritheanna ar feitheamh agus tosaíonn an chéad rith eile toradh nua.',
    changesPrompt:
      'Cabhraigh liom an rud a ritheann {kind} {id} ({name}) air a athrú. Léigh a naisc ar dtús, ansin mol an t-athrú dom lena cheadú.',
    pausedCodeChanged:
      'D’athraigh duine éigin {thing} tar éis do {name} {connection} a nascadh. Tá sé curtha ar sos do gach duine go dtí go ndeimhníonn {name} an nasc ina chomhrá.',
    pausedCodeChangedColleague:
      'D’athraigh duine éigin {thing} tar éis a nasc le {connection} a dheimhniú. Tá sé curtha ar sos do gach duine go dtí go ndeimhnítear an nasc arís ina chomhrá.',
    pausedCodeChangedYou:
      'D’athraigh duine éigin {thing} tar éis duit {connection} a nascadh. Tá sé curtha ar sos do gach duine go dtí go ndeimhneoidh tú an nasc ina chomhrá.',
    fixInChatConfirmBody: 'Iarr ar Chat é a dheimhniú le do rochtain.',
  },
  it: {
    eyebrowShare: 'Condividi',
    eyebrowShared: 'Condiviso con te',
    close: 'Chiudi',
    share: 'Condividi',
    shared: 'Condivisa',
    shareWith: 'Condividi: {audience}',
    whoTitle: 'Chi può aprirlo',
    onlyYou: 'Solo tu',
    onlyYouBody: 'Nessun altro può aprirlo',
    everyone: 'Tutti in {company}',
    everyoneBody: 'Chiunque nell’azienda può aprirlo',
    specificPeople: 'Persone specifiche',
    specificPeopleBody: 'Scegli chi può aprirlo',
    companyNeedsEdit: 'Solo chi può modificarlo può condividerlo con tutti.',
    privateNeedsOwner: 'Solo il proprietario o un amministratore può renderlo nuovamente privato.',
    skillCompany: 'Una competenza raggiunge tutti quando viene pubblicata.',
    peopleCount: 'one:persona|other:persone',
    addPeople: 'Aggiungi persone per nome o email',
    noMatches: 'Nessun collega corrisponde alla ricerca.',
    noColleagues: 'Nessun altro collega ha ancora effettuato l’accesso.',
    loadingPeople: 'Caricamento dei colleghi…',
    peopleFailed: 'Non è stato possibile caricare i tuoi colleghi. Chiudi e riprova.',
    owner: 'Proprietario',
    you: '{name} (tu)',
    canUse: 'Può usare',
    canUseBody: 'Aprirlo e usare la versione attuale.',
    canEdit: 'Può modificare',
    canEditBody: 'Modificarlo e cambiare ciò su cui viene eseguito, tramite la chat.',
    canView: 'Può visualizzare',
    canViewBody: 'Leggerlo e seguire le nuove risposte.',
    canEditFile: 'Modificalo.',
    removeAccess: 'Rimuovi accesso',
    accessFor: 'Accesso per {name}',
    everyoneRowBody: 'Chiunque nell’azienda può trovarlo e aprirlo',
    yourAccess: 'Il tuo accesso',
    sharedBy: '{name} lo ha condiviso con te',
    sharedWithYou: 'È stato condiviso con te',
    ownerDecides: '{name} ne è il proprietario e decide chi può aprirlo.',
    runsOn: 'Eseguito su',
    runsOnHelp:
      'Queste connessioni appartengono a {thing}. Chi lo apre vede ciò che mostra, ma non ottiene mai gli account.',
    runsOnViewerHelp:
      'Usi queste connessioni tramite {thing}. Non ottieni gli account e non vengono mai usati i tuoi.',
    runsOnEmpty: 'Non usa ancora alcuna connessione.',
    runsOnFailed: 'Non è stato possibile caricare su cosa viene eseguito.',
    yours: 'Tuo',
    from: 'Da {name}',
    fromColleague: 'Da un collega',
    reads: 'Legge {list}',
    readsNothing: 'Non legge ancora nulla',
    canChange: 'può apportare modifiche',
    paused: 'In pausa',
    pausedBody:
      'L’accesso di {name} è terminato, quindi al momento {thing} non può leggere {connection} per nessuno. Non passerà automaticamente all’account di qualcun altro.',
    pausedByColleague:
      'L’accesso utilizzato è terminato, quindi al momento {thing} non può leggere {connection} per nessuno. Non passerà automaticamente all’account di qualcun altro.',
    fixInChat: 'Correggi nella chat',
    fixInChatBody: 'Chiedi a Chat di usare il tuo accesso.',
    chatHint: 'Per aggiungere o modificare una connessione, chiedilo nella chat.',
    changeWithChat: 'Modifica con Chat',
    editInChat: 'Modifica nella chat',
    unnamedConnection: 'Connessione senza nome',
    retry: 'Riprova',
    cancel: 'Annulla',
    save: 'Salva',
    done: 'Fatto',
    saving: 'Salvataggio…',
    statusPrivate: 'Solo tu puoi aprirlo.',
    statusCompany: 'Tutti in {company} possono aprirlo.',
    statusPeople: 'Condiviso con {people}.',
    statusPeopleEmpty: 'Aggiungi persone per condividerlo.',
    statusPending: 'Nulla cambia finché non salvi.',
    theApp: 'l’app',
    theAgent: 'l’agente',
    theSkill: 'la competenza',
    theResult: 'il risultato',
    theItem: 'esso',
    untitled: 'Senza titolo',
    updated: 'Condivisione aggiornata.',
    agentNoOwner:
      'Il proprietario ha lasciato l’azienda, quindi l’agente resta condiviso. Disattivalo o archivialo.',
    agentPersonal:
      'Questa scelta rende l’agente privato per il proprietario: viene disattivato, le esecuzioni in attesa terminano e la successiva avvia un nuovo risultato.',
    changesPrompt:
      'Aiutami a cambiare su cosa viene eseguito {kind} {id} ({name}). Prima leggi le connessioni, poi proponimi la modifica perché la approvi.',
    pausedCodeChanged:
      'Qualcuno ha modificato {thing} dopo che {name} ha collegato {connection}. È in pausa per tutti finché {name} non conferma la connessione nella sua chat.',
    pausedCodeChangedColleague:
      'Qualcuno ha modificato {thing} dopo la conferma della sua connessione a {connection}. È in pausa per tutti finché la connessione non viene confermata di nuovo nella sua chat.',
    pausedCodeChangedYou:
      'Qualcuno ha modificato {thing} dopo che hai collegato {connection}. È in pausa per tutti finché non confermi la connessione nella sua chat.',
    fixInChatConfirmBody: 'Chiedi alla chat di confermarlo con il tuo accesso.',
  },
  lv: {
    eyebrowShare: 'Kopīgot',
    eyebrowShared: 'Kopīgots ar jums',
    close: 'Aizvērt',
    share: 'Kopīgot',
    shared: 'Kopīgota',
    shareWith: 'Kopīgot: {audience}',
    whoTitle: 'Kas to var atvērt',
    onlyYou: 'Tikai jūs',
    onlyYouBody: 'Neviens cits to nevar atvērt',
    everyone: 'Visi uzņēmumā “{company}”',
    everyoneBody: 'To var atvērt ikviens uzņēmumā',
    specificPeople: 'Konkrēti cilvēki',
    specificPeopleBody: 'Izvēlieties, kas to var atvērt',
    companyNeedsEdit: 'Tikai personas, kas to var rediģēt, var to kopīgot ar visiem.',
    privateNeedsOwner: 'Tikai īpašnieks vai administrators var atkal padarīt to privātu.',
    skillCompany: 'Prasme kļūst pieejama visiem, kad tā tiek publicēta.',
    peopleCount: 'zero:cilvēku|one:cilvēks|other:cilvēki',
    addPeople: 'Pievienojiet personas pēc vārda vai e-pasta',
    noMatches: 'Neviens kolēģis neatbilst meklēšanas kritērijiem.',
    noColleagues: 'Neviens cits kolēģis vēl nav pievienojies.',
    loadingPeople: 'Notiek kolēģu ielāde…',
    peopleFailed: 'Neizdevās ielādēt jūsu kolēģus. Aizveriet un mēģiniet vēlreiz.',
    owner: 'Īpašnieks',
    you: '{name} (jūs)',
    canUse: 'Var izmantot',
    canUseBody: 'Atvērt un izmantot pašreizējo versiju.',
    canEdit: 'Var rediģēt',
    canEditBody: 'Mainīt to un tā darbības iestatījumus, izmantojot tā tērzēšanu.',
    canView: 'Var skatīt',
    canViewBody: 'Lasīt to un sekot jaunām atbildēm.',
    canEditFile: 'Mainīt to.',
    removeAccess: 'Noņemt piekļuvi',
    accessFor: 'Piekļuve lietotājam {name}',
    everyoneRowBody: 'Ikviens uzņēmumā var to atrast un atvērt',
    yourAccess: 'Jūsu piekļuve',
    sharedBy: '{name} to kopīgoja ar jums',
    sharedWithYou: 'Tas tika kopīgots ar jums',
    ownerDecides: '{name} ir tā īpašnieks un nosaka, kas to var atvērt.',
    runsOn: 'Izmanto',
    runsOnHelp:
      'Šie savienojumi pieder vienumam {thing}. Personas, kas to atver, redz, ko tas rāda, bet nekad neiegūst piekļuvi kontiem.',
    runsOnViewerHelp:
      'Šos savienojumus izmantojat, izmantojot {thing}. Jūs neiegūstat piekļuvi kontiem, un tas nekad neizmanto jūsu kontus.',
    runsOnEmpty: 'Tas vēl neizmanto nevienu savienojumu.',
    runsOnFailed: 'Neizdevās ielādēt informāciju par tā izmantotajiem savienojumiem.',
    yours: 'Jūsu',
    from: 'No {name}',
    fromColleague: 'No kolēģa',
    reads: 'Lasa {list}',
    readsNothing: 'Pagaidām neko nelasa',
    canChange: 'var veikt izmaiņas',
    paused: 'Apturēts',
    pausedBody:
      'Lietotāja {name} piekļuve ir beigusies, tāpēc {thing} pašlaik nevienam nevar nolasīt {connection}. Tas pats no sevis nepārslēgsies uz cita lietotāja kontu.',
    pausedByColleague:
      'Piekļuve, ko tas izmantoja, ir beigusies, tāpēc {thing} pašlaik nevienam nevar nolasīt {connection}. Tas pats no sevis nepārslēgsies uz cita lietotāja kontu.',
    fixInChat: 'Labot tērzēšanā',
    fixInChatBody: 'Lūdziet Chat izmantot jūsu piekļuvi.',
    chatHint: 'Lai pievienotu vai mainītu savienojumu, jautājiet tā tērzēšanā.',
    changeWithChat: 'Mainīt ar Chat',
    editInChat: 'Rediģēt tērzēšanā',
    unnamedConnection: 'Savienojums bez nosaukuma',
    retry: 'Mēģināt vēlreiz',
    cancel: 'Atcelt',
    save: 'Saglabāt',
    done: 'Pabeigts',
    saving: 'Notiek saglabāšana…',
    statusPrivate: 'To varat atvērt tikai jūs.',
    statusCompany: 'To var atvērt ikviens uzņēmumā {company}.',
    statusPeople: 'Kopīgots ar: {people}.',
    statusPeopleEmpty: 'Pievienojiet personas, ar kurām to kopīgot.',
    statusPending: 'Nekas nemainīsies, kamēr nesaglabāsiet.',
    theApp: 'lietotni',
    theAgent: 'aģentu',
    theSkill: 'prasmi',
    theResult: 'rezultāts',
    theItem: 'tas',
    untitled: 'Bez nosaukuma',
    updated: 'Kopīgošana atjaunināta.',
    agentNoOwner:
      'Īpašnieks ir aizgājis, tāpēc aģents paliek kopīgots. Izslēdziet vai arhivējiet to.',
    agentPersonal:
      'Šī izvēle padara aģentu privātu tā īpašniekam: tas tiek izslēgts, gaidošās izpildes beidzas un nākamā sāk jaunu rezultātu.',
    changesPrompt:
      'Palīdzi man mainīt, uz kā darbojas {kind} {id} ({name}). Vispirms izlasi tā savienojumus, pēc tam ierosini izmaiņas, lai es tās apstiprinātu.',
    pausedCodeChanged:
      'Kāds mainīja {thing} pēc tam, kad {name} savienoja {connection}. Tas ir apturēts visiem, līdz {name} apstiprinās savienojumu tā tērzēšanā.',
    pausedCodeChangedColleague:
      'Kāds mainīja {thing} pēc tam, kad tā savienojums ar {connection} tika apstiprināts. Tas ir apturēts visiem, līdz savienojums atkal tiks apstiprināts tā tērzēšanā.',
    pausedCodeChangedYou:
      'Kāds mainīja {thing} pēc tam, kad jūs savienojāt {connection}. Tas ir apturēts visiem, līdz apstiprināsiet savienojumu tā tērzēšanā.',
    fixInChatConfirmBody: 'Lūdziet Chat to apstiprināt ar jūsu piekļuvi.',
  },
  lt: {
    eyebrowShare: 'Bendrinti',
    eyebrowShared: 'Bendrinama su jumis',
    close: 'Uždaryti',
    share: 'Bendrinti',
    shared: 'Bendrinama',
    shareWith: 'Bendrinti: {audience}',
    whoTitle: 'Kas gali atidaryti',
    onlyYou: 'Tik jūs',
    onlyYouBody: 'Niekas kitas negali atidaryti',
    everyone: 'Visi įmonėje „{company}“',
    everyoneBody: 'Atidaryti gali bet kas įmonėje',
    specificPeople: 'Konkretūs žmonės',
    specificPeopleBody: 'Pasirinkite, kas gali atidaryti',
    companyNeedsEdit: 'Bendrinti su visais gali tik žmonės, galintys tai redaguoti.',
    privateNeedsOwner: 'Padaryti tai privačiu gali tik savininkas arba administratorius.',
    skillCompany: 'Įgūdis tampa prieinamas visiems, kai yra paskelbiamas.',
    peopleCount: 'one:žmogus|few:žmonės|many:žmogaus|other:žmonių',
    addPeople: 'Pridėkite žmones pagal vardą arba el. pašto adresą',
    noMatches: 'Atitinkančių kolegų nerasta.',
    noColleagues: 'Kol kas neprisijungė jokių kitų kolegų.',
    loadingPeople: 'Įkeliami kolegos…',
    peopleFailed: 'Nepavyko įkelti jūsų kolegų. Uždarykite ir bandykite dar kartą.',
    owner: 'Savininkas',
    you: '{name} (jūs)',
    canUse: 'Gali naudoti',
    canUseBody: 'Atidaryti ir naudoti dabartinę versiją.',
    canEdit: 'Gali redaguoti',
    canEditBody: 'Keisti ją ir tai, su kuo ji veikia, naudodamiesi jos pokalbiu.',
    canView: 'Gali peržiūrėti',
    canViewBody: 'Skaityti ją ir sekti naujus atsakymus.',
    canEditFile: 'Keisti ją.',
    removeAccess: 'Pašalinti prieigą',
    accessFor: '{name} prieiga',
    everyoneRowBody: 'Bet kas įmonėje gali ją rasti ir atidaryti',
    yourAccess: 'Jūsų prieiga',
    sharedBy: '{name} bendrino tai su jumis',
    sharedWithYou: 'Tai bendrinama su jumis',
    ownerDecides: '{name} yra savininkas ir sprendžia, kas gali atidaryti.',
    runsOn: 'Veikia su',
    runsOnHelp:
      'Šie ryšiai priklauso {thing}. Ją atidarę žmonės mato, ką ji rodo, bet niekada negauna paskyrų prieigos duomenų.',
    runsOnViewerHelp:
      'Šiuos ryšius naudojate per {thing}. Paskyrų prieigos duomenų negaunate, o jūsų paskyra niekada nenaudojama.',
    runsOnEmpty: 'Kol kas nenaudojami jokie ryšiai.',
    runsOnFailed: 'Nepavyko įkelti, su kuo ji veikia.',
    yours: 'Jūsų',
    from: 'Nuo {name}',
    fromColleague: 'Nuo kolegos',
    reads: 'Skaito {list}',
    readsNothing: 'Kol kas nieko neskaito',
    canChange: 'gali atlikti pakeitimus',
    paused: 'Pristabdyta',
    pausedBody:
      '{name} prieiga baigėsi, todėl {thing} šiuo metu negali niekam nuskaityti {connection}. Ji automatiškai nepersijungs prie kito asmens paskyros.',
    pausedByColleague:
      'Naudota prieiga baigėsi, todėl {thing} šiuo metu negali niekam nuskaityti {connection}. Ji automatiškai nepersijungs prie kito asmens paskyros.',
    fixInChat: 'Pataisyti pokalbyje',
    fixInChatBody: 'Paprašykite Chat vietoj jos naudoti jūsų prieigą.',
    chatHint: 'Norėdami pridėti arba pakeisti ryšį, paklauskite jos pokalbyje.',
    changeWithChat: 'Keisti naudojant Chat',
    editInChat: 'Redaguoti pokalbyje',
    unnamedConnection: 'Nepavadintas ryšys',
    retry: 'Bandyti dar kartą',
    cancel: 'Atšaukti',
    save: 'Išsaugoti',
    done: 'Atlikta',
    saving: 'Išsaugoma…',
    statusPrivate: 'Tik jūs galite tai atidaryti.',
    statusCompany: 'Visi įmonėje {company} gali tai atidaryti.',
    statusPeople: 'Bendrinama su: {people}.',
    statusPeopleEmpty: 'Pridėkite žmonių, kad bendrintumėte.',
    statusPending: 'Pakeitimai įsigalios tik išsaugojus.',
    theApp: 'programa',
    theAgent: 'agentas',
    theSkill: 'įgūdis',
    theResult: 'rezultatas',
    theItem: 'elementas',
    untitled: 'Be pavadinimo',
    updated: 'Bendrinimas atnaujintas.',
    agentNoOwner:
      'Savininkas išėjo, todėl agentas lieka bendrinamas. Išjunkite arba archyvuokite jį.',
    agentPersonal:
      'Šis pasirinkimas padaro agentą privatų jo savininkui: jis išjungiamas, laukiančios vykdymo užduotys baigiamos, o kita pradeda naują rezultatą.',
    changesPrompt:
      'Padėkite man pakeisti, kuo veikia {kind} {id} ({name}). Pirmiausia perskaitykite jo ryšius, tada pasiūlykite pakeitimą, kurį galėčiau patvirtinti.',
    pausedCodeChanged:
      'Kažkas pakeitė {thing}, kai {name} prijungė {connection}. Pristabdyta visiems, kol {name} patvirtins ryšį pokalbyje.',
    pausedCodeChangedColleague:
      'Kažkas pakeitė {thing}, kai jo ryšys su {connection} buvo patvirtintas. Pristabdyta visiems, kol ryšys vėl bus patvirtintas pokalbyje.',
    pausedCodeChangedYou:
      'Kažkas pakeitė {thing}, kai prijungėte {connection}. Pristabdyta visiems, kol patvirtinsite ryšį pokalbyje.',
    fixInChatConfirmBody: 'Paprašykite Chat patvirtinti tai naudojant jūsų prieigą.',
  },
  mt: {
    eyebrowShare: 'Aqsam',
    eyebrowShared: 'Maqsum miegħek',
    close: 'Agħlaq',
    share: 'Aqsam',
    shared: 'Maqsuma',
    shareWith: 'Aqsam: {audience}',
    whoTitle: "Min jista' jiftħu",
    onlyYou: 'Int biss',
    onlyYouBody: "Ħaddieħor ma jista' jiftħu",
    everyone: 'Kulħadd f’{company}',
    everyoneBody: "Kulħadd fil-kumpanija jista' jiftħu",
    specificPeople: 'Persuni speċifiċi',
    specificPeopleBody: "Agħżel min jista' jiftħu",
    companyNeedsEdit: "Dawk biss li jistgħu jeditjawh jistgħu jaqsmuh ma' kulħadd.",
    privateNeedsOwner: "Is-sid tiegħu jew amministratur biss jista' jerġa' jagħmlu privat.",
    skillCompany: 'Ħila tasal għand kulħadd meta tiġi ppubblikata.',
    peopleCount: 'one:persuna|two:persuni|few:persuni|many:persuna|other:persuna',
    addPeople: 'Żid nies bl-isem jew bl-email',
    noMatches: 'Ma nstab l-ebda kollega li jaqbel.',
    noColleagues: 'Għadu ma ngħaqad l-ebda kollega ieħor.',
    loadingPeople: 'Qed jitgħabbew il-kollegi…',
    peopleFailed: "Ma stajniex intellgħu l-kollegi tiegħek. Agħlaq u erġa' pprova.",
    owner: 'Sid',
    you: '{name} (int)',
    canUse: "Jista' jużah",
    canUseBody: 'Iftaħ u uża l-verżjoni attwali.',
    canEdit: "Jista' jeditjah",
    canEditBody: 'Ibdelha u dak li jaħdem fuqu, permezz taċ-chat tiegħu.',
    canView: "Jista' jarah",
    canViewBody: 'Aqrah u segwi tweġibiet ġodda.',
    canEditFile: 'Ibdelha.',
    removeAccess: 'Neħħi l-aċċess',
    accessFor: 'Aċċess għal {name}',
    everyoneRowBody: "Kulħadd fil-kumpanija jista' jsibu u jiftħu",
    yourAccess: 'L-aċċess tiegħek',
    sharedBy: '{name} qasmu miegħek',
    sharedWithYou: 'Ġie maqsum miegħek',
    ownerDecides: "{name} huwa s-sid u jiddeċiedi min jista' jiftħu.",
    runsOn: 'Jaħdem fuq',
    runsOnHelp:
      'Dawn il-konnessjonijiet jappartjenu lil {thing}. Dawk li jiftħuh jaraw dak li juri, iżda qatt ma jiksbu l-kontijiet.',
    runsOnViewerHelp:
      "Tuża dawn il-konnessjonijiet permezz ta' {thing}. Ma jkollokx il-kontijiet, u qatt ma juża tiegħek.",
    runsOnEmpty: 'Għadu ma juża l-ebda konnessjoni.',
    runsOnFailed: 'Ma stajniex intellgħu fuq xiex jaħdem.',
    yours: 'Tiegħek',
    from: 'Minn {name}',
    fromColleague: 'Minn kollega',
    reads: 'Jaqra {list}',
    readsNothing: 'Għadu ma jaqra xejn',
    canChange: "jista' jagħmel bidliet",
    paused: 'Imwaqqaf temporanjament',
    pausedBody:
      "L-aċċess ta' {name} spiċċa, għalhekk {thing} bħalissa ma jistax jaqra {connection} għal ħadd. Mhux se jaqleb waħdu għall-kont ta' ħaddieħor.",
    pausedByColleague:
      "L-aċċess li kien juża spiċċa, għalhekk {thing} bħalissa ma jistax jaqra {connection} għal ħadd. Mhux se jaqleb waħdu għall-kont ta' ħaddieħor.",
    fixInChat: 'Irranġa fiċ-chat',
    fixInChatBody: 'Staqsi lil Chat juża l-aċċess tiegħek minflok.',
    chatHint: 'Biex iżżid jew tibdel konnessjoni, staqsi fiċ-chat tagħha.',
    changeWithChat: "Ibdel ma' Chat",
    editInChat: 'Editja fiċ-chat',
    unnamedConnection: 'Konnessjoni bla isem',
    retry: "Erġa' pprova",
    cancel: 'Ikkanċella',
    save: 'Issejvja',
    done: 'Lest',
    saving: 'Qed jiġi ssejvjat…',
    statusPrivate: "Int biss tista' tiftaħ.",
    statusCompany: "Kulħadd għand {company} jista' jiftħu.",
    statusPeople: "Maqsum ma' {people}.",
    statusPeopleEmpty: 'Żid nies biex taqsmu.',
    statusPending: 'Xejn ma jinbidel qabel ma ssalva.',
    theApp: 'l-App',
    theAgent: 'l-Aġent',
    theSkill: 'il-Ħila',
    theResult: 'ir-riżultat',
    theItem: 'dan',
    untitled: 'Bla titlu',
    updated: 'Il-qsim ġie aġġornat.',
    agentNoOwner: 'Is-sid telaq, għalhekk l-aġent jibqa’ maqsum. Itfih jew arkivjah.',
    agentPersonal:
      'Din l-għażla tagħmel l-aġent privat għal sidu: jintefa, jintemmu l-eżekuzzjonijiet li qed jistennew u dik li jmiss tibda riżultat ġdid.',
    changesPrompt:
      'Għinni nbiddel fuq xiex jaħdem {kind} {id} ({name}). Aqra l-konnessjonijiet tiegħu l-ewwel, imbagħad ipproponi l-bidla biex napprovaha.',
    pausedCodeChanged:
      'Xi ħadd biddel {thing} wara li {name} qabbad {connection}. Imwaqqaf għal kulħadd sakemm {name} jikkonferma l-konnessjoni fiċ-chat tiegħu.',
    pausedCodeChangedColleague:
      'Xi ħadd biddel {thing} wara li l-konnessjoni tiegħu ma’ {connection} ġiet ikkonfermata. Imwaqqaf għal kulħadd sakemm il-konnessjoni terġa’ tiġi kkonfermata fiċ-chat tiegħu.',
    pausedCodeChangedYou:
      'Xi ħadd biddel {thing} wara li qabbadt {connection}. Imwaqqaf għal kulħadd sakemm tikkonferma l-konnessjoni fiċ-chat tiegħu.',
    fixInChatConfirmBody: 'Itlob liċ-chat jikkonfermah bl-aċċess tiegħek.',
  },
  pl: {
    eyebrowShare: 'Udostępnij',
    eyebrowShared: 'Udostępniono Ci',
    close: 'Zamknij',
    share: 'Udostępnij',
    shared: 'Udostępniona',
    shareWith: 'Udostępnij: {audience}',
    whoTitle: 'Kto może to otworzyć',
    onlyYou: 'Tylko ty',
    onlyYouBody: 'Nikt inny nie może tego otworzyć',
    everyone: 'Wszyscy w firmie {company}',
    everyoneBody: 'Każdy w firmie może to otworzyć',
    specificPeople: 'Wybrane osoby',
    specificPeopleBody: 'Wybierz, kto może to otworzyć',
    companyNeedsEdit: 'Tylko osoby, które mogą to edytować, mogą udostępnić to wszystkim.',
    privateNeedsOwner: 'Tylko właściciel lub administrator może ponownie ustawić to jako prywatne.',
    skillCompany: 'Umiejętność jest dostępna dla wszystkich po opublikowaniu.',
    peopleCount: 'one:osoba|few:osoby|many:osób|other:osoby',
    addPeople: 'Dodaj osoby po nazwie lub adresie e-mail',
    noMatches: 'Nie znaleziono pasującego współpracownika.',
    noColleagues: 'Nie dołączyli jeszcze inni współpracownicy.',
    loadingPeople: 'Ładowanie współpracowników…',
    peopleFailed: 'Nie udało się wczytać współpracowników. Zamknij i spróbuj ponownie.',
    owner: 'Właściciel',
    you: '{name} (Ty)',
    canUse: 'Może używać',
    canUseBody: 'Otwierać i używać bieżącej wersji.',
    canEdit: 'Może edytować',
    canEditBody: 'Zmieniać to i sposób jego działania za pośrednictwem czatu.',
    canView: 'Może wyświetlać',
    canViewBody: 'Czytać i śledzić nowe odpowiedzi.',
    canEditFile: 'Zmieniaj to.',
    removeAccess: 'Usuń dostęp',
    accessFor: 'Dostęp dla {name}',
    everyoneRowBody: 'Każdy w firmie może to znaleźć i otworzyć',
    yourAccess: 'Twój dostęp',
    sharedBy: '{name} udostępnił(a) Ci to',
    sharedWithYou: 'Udostępniono Ci to',
    ownerDecides: 'Właścicielem jest {name}, który decyduje, kto może to otworzyć.',
    runsOn: 'Korzysta z',
    runsOnHelp:
      'Te połączenia należą do: {thing}. Osoby, które to otworzą, zobaczą wyświetlane informacje, ale nigdy nie uzyskają dostępu do kont.',
    runsOnViewerHelp:
      'Korzystasz z tych połączeń za pośrednictwem: {thing}. Nie uzyskujesz dostępu do kont i to nigdy nie korzysta z Twoich kont.',
    runsOnEmpty: 'Nie korzysta jeszcze z żadnych połączeń.',
    runsOnFailed: 'Nie udało się wczytać informacji, z czego to korzysta.',
    yours: 'Twoje',
    from: 'Od {name}',
    fromColleague: 'Od współpracownika',
    reads: 'Odczytuje: {list}',
    readsNothing: 'Jeszcze niczego nie odczytuje',
    canChange: 'może wprowadzać zmiany',
    paused: 'Wstrzymano',
    pausedBody:
      'Dostęp użytkownika {name} wygasł, więc {thing} nie może teraz odczytywać {connection} dla nikogo. Nie przełączy się samodzielnie na konto innej osoby.',
    pausedByColleague:
      'Dostęp, z którego korzystało, wygasł, więc {thing} nie może teraz odczytywać {connection} dla nikogo. Nie przełączy się samodzielnie na konto innej osoby.',
    fixInChat: 'Napraw na czacie',
    fixInChatBody: 'Poproś Chat, aby zamiast tego używał Twojego dostępu.',
    chatHint: 'Aby dodać lub zmienić połączenie, poproś o to na jego czacie.',
    changeWithChat: 'Zmień za pomocą czatu',
    editInChat: 'Edytuj na czacie',
    unnamedConnection: 'Połączenie bez nazwy',
    retry: 'Spróbuj ponownie',
    cancel: 'Anuluj',
    save: 'Zapisz',
    done: 'Gotowe',
    saving: 'Zapisywanie…',
    statusPrivate: 'Tylko Ty możesz to otworzyć.',
    statusCompany: 'Każdy w firmie {company} może to otworzyć.',
    statusPeople: 'Udostępniono: {people}.',
    statusPeopleEmpty: 'Dodaj osoby, aby to udostępnić.',
    statusPending: 'Nic się nie zmieni, dopóki nie zapiszesz.',
    theApp: 'Aplikacja',
    theAgent: 'Agent',
    theSkill: 'Umiejętność',
    theResult: 'wynik',
    theItem: 'element',
    untitled: 'Bez tytułu',
    updated: 'Udostępnianie zaktualizowane.',
    agentNoOwner:
      'Właściciel odszedł, więc agent pozostaje udostępniony. Wyłącz go lub zarchiwizuj.',
    agentPersonal:
      'Ten wybór czyni agenta prywatnym dla właściciela: agent zostaje wyłączony, oczekujące uruchomienia kończą się, a następne rozpoczyna nowy wynik.',
    changesPrompt:
      'Pomóż mi zmienić, na czym działa {kind} {id} ({name}). Najpierw odczytaj jego połączenia, a następnie zaproponuj zmianę do zatwierdzenia.',
    pausedCodeChanged:
      'Ktoś zmienił: {thing}, po tym jak {name} połączył(a) {connection}. Jest wstrzymane dla wszystkich, dopóki {name} nie potwierdzi połączenia na jego czacie.',
    pausedCodeChangedColleague:
      'Ktoś zmienił: {thing}, po potwierdzeniu połączenia z {connection}. Jest wstrzymane dla wszystkich, dopóki połączenie nie zostanie ponownie potwierdzone na jego czacie.',
    pausedCodeChangedYou:
      'Ktoś zmienił: {thing}, po tym jak połączyłeś(-aś) {connection}. Jest wstrzymane dla wszystkich, dopóki nie potwierdzisz połączenia na jego czacie.',
    fixInChatConfirmBody: 'Poproś Chat o potwierdzenie z użyciem Twojego dostępu.',
  },
  pt: {
    eyebrowShare: 'Compartilhar',
    eyebrowShared: 'Compartilhado com você',
    close: 'Fechar',
    share: 'Partilhar',
    shared: 'Partilhada',
    shareWith: 'Partilhar: {audience}',
    whoTitle: 'Quem pode abrir',
    onlyYou: 'Só para si',
    onlyYouBody: 'Ninguém mais pode abrir',
    everyone: 'Todos em {company}',
    everyoneBody: 'Qualquer pessoa da empresa pode abrir',
    specificPeople: 'Pessoas específicas',
    specificPeopleBody: 'Escolha quem pode abrir',
    companyNeedsEdit: 'Somente pessoas que podem editar podem compartilhá-lo com todos.',
    privateNeedsOwner:
      'Somente o proprietário ou um administrador pode torná-lo privado novamente.',
    skillCompany: 'Uma habilidade chega a todos quando é publicada.',
    peopleCount: 'one:pessoa|other:pessoas',
    addPeople: 'Adicionar pessoas por nome ou e-mail',
    noMatches: 'Nenhum colega corresponde a isso.',
    noColleagues: 'Ainda não há outros colegas que tenham entrado.',
    loadingPeople: 'Carregando colegas…',
    peopleFailed: 'Não foi possível carregar seus colegas. Feche e tente novamente.',
    owner: 'Proprietário',
    you: '{name} (você)',
    canUse: 'Pode usar',
    canUseBody: 'Abrir e usar a versão atual.',
    canEdit: 'Pode editar',
    canEditBody: 'Alterar o conteúdo e o que ele executa pelo chat.',
    canView: 'Pode visualizar',
    canViewBody: 'Ler e acompanhar novas respostas.',
    canEditFile: 'Alterar.',
    removeAccess: 'Remover acesso',
    accessFor: 'Acesso de {name}',
    everyoneRowBody: 'Qualquer pessoa da empresa pode encontrá-lo e abri-lo',
    yourAccess: 'Seu acesso',
    sharedBy: '{name} compartilhou com você',
    sharedWithYou: 'Foi compartilhado com você',
    ownerDecides: '{name} é o proprietário e decide quem pode abrir.',
    runsOn: 'Usa',
    runsOnHelp:
      'Essas conexões pertencem a {thing}. Quem abrir verá o que elas mostram, mas nunca terá acesso às contas.',
    runsOnViewerHelp:
      'Você usa essas conexões por meio de {thing}. Você não recebe acesso às contas, e ele nunca usa as suas.',
    runsOnEmpty: 'Ainda não usa nenhuma conexão.',
    runsOnFailed: 'Não foi possível carregar o que ele usa.',
    yours: 'Suas',
    from: 'De {name}',
    fromColleague: 'De um colega',
    reads: 'Lê {list}',
    readsNothing: 'Ainda não lê nada',
    canChange: 'pode fazer alterações',
    paused: 'Pausado',
    pausedBody:
      'O acesso de {name} terminou, então {thing} não pode ler {connection} para ninguém no momento. Ele não mudará sozinho para a conta de outra pessoa.',
    pausedByColleague:
      'O acesso usado terminou, então {thing} não pode ler {connection} para ninguém no momento. Ele não mudará sozinho para a conta de outra pessoa.',
    fixInChat: 'Corrigir no chat',
    fixInChatBody: 'Peça ao Chat para usar seu próprio acesso.',
    chatHint: 'Para adicionar ou alterar uma conexão, peça no chat correspondente.',
    changeWithChat: 'Alterar com o Chat',
    editInChat: 'Editar no chat',
    unnamedConnection: 'Conexão sem nome',
    retry: 'Tentar novamente',
    cancel: 'Cancelar',
    save: 'Salvar',
    done: 'Concluído',
    saving: 'Salvando…',
    statusPrivate: 'Somente você pode abrir.',
    statusCompany: 'Todos em {company} podem abrir.',
    statusPeople: 'Compartilhado com {people}.',
    statusPeopleEmpty: 'Adicione pessoas para compartilhar.',
    statusPending: 'Nada muda até você salvar.',
    theApp: 'o aplicativo',
    theAgent: 'o agente',
    theSkill: 'a habilidade',
    theResult: 'o resultado',
    theItem: 'isso',
    untitled: 'Sem título',
    updated: 'Partilha atualizada.',
    agentNoOwner:
      'O proprietário saiu, pelo que o agente continua partilhado. Desative-o ou arquive-o.',
    agentPersonal:
      'Esta opção torna o agente privado para o proprietário: é desativado, as execuções em espera terminam e a seguinte inicia um novo resultado.',
    changesPrompt:
      'Ajude-me a alterar onde {kind} {id} ({name}) é executado. Leia primeiro as conexões e depois proponha a alteração para eu aprovar.',
    pausedCodeChanged:
      'Alguém alterou {thing} depois que {name} conectou {connection}. Está em pausa para todos até {name} confirmar a conexão no chat dele.',
    pausedCodeChangedColleague:
      'Alguém alterou {thing} depois que a conexão dele com {connection} foi confirmada. Está em pausa para todos até a conexão ser confirmada novamente no chat dele.',
    pausedCodeChangedYou:
      'Alguém alterou {thing} depois que você conectou {connection}. Está em pausa para todos até você confirmar a conexão no chat dele.',
    fixInChatConfirmBody: 'Peça ao chat para confirmar com o seu acesso.',
  },
  ro: {
    eyebrowShare: 'Partajează',
    eyebrowShared: 'Partajat cu tine',
    close: 'Închide',
    share: 'Distribuiți',
    shared: 'Partajată',
    shareWith: 'Distribuiți: {audience}',
    whoTitle: 'Cine îl poate deschide',
    onlyYou: 'Doar tu',
    onlyYouBody: 'Nimeni altcineva nu îl poate deschide',
    everyone: 'Toată lumea din {company}',
    everyoneBody: 'Oricine din companie îl poate deschide',
    specificPeople: 'Anumite persoane',
    specificPeopleBody: 'Alege cine îl poate deschide',
    companyNeedsEdit: 'Doar persoanele care îl pot edita îl pot partaja cu toată lumea.',
    privateNeedsOwner: 'Doar proprietarul sau un administrator îl poate face din nou privat.',
    skillCompany: 'O abilitate ajunge la toată lumea când este publicată.',
    peopleCount: 'one:persoană|few:persoane|other:de persoane',
    addPeople: 'Adaugă persoane după nume sau e-mail',
    noMatches: 'Niciun coleg nu corespunde.',
    noColleagues: 'Niciun alt coleg nu s-a alăturat încă.',
    loadingPeople: 'Se încarcă colegii…',
    peopleFailed: 'Nu am putut încărca colegii. Închide și încearcă din nou.',
    owner: 'Proprietar',
    you: '{name} (tu)',
    canUse: 'Poate utiliza',
    canUseBody: 'Îl poate deschide și utiliza versiunea actuală.',
    canEdit: 'Poate edita',
    canEditBody: 'Îl poate modifica și poate schimba ceea ce rulează, prin chatul său.',
    canView: 'Poate vedea',
    canViewBody: 'Îl poate citi și urmări răspunsurile noi.',
    canEditFile: 'Modifică-l.',
    removeAccess: 'Elimină accesul',
    accessFor: 'Acces pentru {name}',
    everyoneRowBody: 'Oricine din companie îl poate găsi și deschide',
    yourAccess: 'Accesul tău',
    sharedBy: '{name} l-a partajat cu tine',
    sharedWithYou: 'A fost partajat cu tine',
    ownerDecides: '{name} este proprietarul și decide cine îl poate deschide.',
    runsOn: 'Rulează pe',
    runsOnHelp:
      'Aceste conexiuni aparțin de {thing}. Persoanele care îl deschid văd ce afișează, dar nu primesc niciodată conturile.',
    runsOnViewerHelp:
      'Utilizezi aceste conexiuni prin {thing}. Nu primești conturile, iar acesta nu le folosește niciodată pe ale tale.',
    runsOnEmpty: 'Încă nu folosește nicio conexiune.',
    runsOnFailed: 'Nu am putut încărca pe ce rulează.',
    yours: 'Al tău',
    from: 'De la {name}',
    fromColleague: 'De la un coleg',
    reads: 'Citește {list}',
    readsNothing: 'Încă nu citește nimic',
    canChange: 'poate face modificări',
    paused: 'În pauză',
    pausedBody:
      'Accesul lui {name} s-a încheiat, așa că {thing} nu poate citi {connection} pentru nimeni momentan. Nu va trece automat la contul altcuiva.',
    pausedByColleague:
      'Accesul folosit s-a încheiat, așa că {thing} nu poate citi {connection} pentru nimeni momentan. Nu va trece automat la contul altcuiva.',
    fixInChat: 'Remediază în chat',
    fixInChatBody: 'Cere-i lui Chat să folosească în schimb propriul tău acces.',
    chatHint: 'Pentru a adăuga sau modifica o conexiune, întreabă în chatul acesteia.',
    changeWithChat: 'Modifică prin Chat',
    editInChat: 'Editează în chat',
    unnamedConnection: 'Conexiune fără nume',
    retry: 'Încearcă din nou',
    cancel: 'Anulează',
    save: 'Salvează',
    done: 'Gata',
    saving: 'Se salvează…',
    statusPrivate: 'Doar tu îl poți deschide.',
    statusCompany: 'Toți cei din {company} îl pot deschide.',
    statusPeople: 'Partajat cu {people}.',
    statusPeopleEmpty: 'Adaugă persoane pentru a-l partaja.',
    statusPending: 'Nimic nu se schimbă până nu salvezi.',
    theApp: 'aplicația',
    theAgent: 'agentul',
    theSkill: 'abilitatea',
    theResult: 'rezultatul',
    theItem: 'elementul',
    untitled: 'Fără titlu',
    updated: 'Partajarea a fost actualizată.',
    agentNoOwner:
      'Proprietarul a plecat, deci agentul rămâne partajat. Dezactivați-l sau arhivați-l.',
    agentPersonal:
      'Această opțiune face agentul privat pentru proprietar: este dezactivat, execuțiile în așteptare se încheie, iar următoarea începe un rezultat nou.',
    changesPrompt:
      'Ajută-mă să schimb pe ce rulează {kind} {id} ({name}). Citește mai întâi conexiunile, apoi propune schimbarea pentru aprobarea mea.',
    pausedCodeChanged:
      'Cineva a modificat {thing} după ce {name} a conectat {connection}. Este întrerupt pentru toți până când {name} confirmă conexiunea în chatul său.',
    pausedCodeChangedColleague:
      'Cineva a modificat {thing} după ce conexiunea sa la {connection} a fost confirmată. Este întrerupt pentru toți până când conexiunea este confirmată din nou în chatul său.',
    pausedCodeChangedYou:
      'Cineva a modificat {thing} după ce ai conectat {connection}. Este întrerupt pentru toți până când confirmi conexiunea în chatul său.',
    fixInChatConfirmBody: 'Cere chatului să confirme cu accesul tău.',
  },
  sk: {
    eyebrowShare: 'Zdieľať',
    eyebrowShared: 'Zdieľané s vami',
    close: 'Zavrieť',
    share: 'Zdieľať',
    shared: 'Zdieľaná',
    shareWith: 'Zdieľať: {audience}',
    whoTitle: 'Kto to môže otvoriť',
    onlyYou: 'Len vy',
    onlyYouBody: 'Nikto iný to nemôže otvoriť',
    everyone: 'Všetci vo firme {company}',
    everyoneBody: 'Môže to otvoriť ktokoľvek v spoločnosti',
    specificPeople: 'Konkrétni ľudia',
    specificPeopleBody: 'Vyberte, kto to môže otvoriť',
    companyNeedsEdit: 'Zdieľať to so všetkými môžu iba ľudia, ktorí to môžu upravovať.',
    privateNeedsOwner: 'Opäť to môže nastaviť ako súkromné iba vlastník alebo správca.',
    skillCompany: 'Zručnosť sa po zverejnení sprístupní všetkým.',
    peopleCount: 'one:človek|few:ľudia|many:ľudí|other:ľudí',
    addPeople: 'Pridajte ľudí podľa mena alebo e-mailu',
    noMatches: 'Nikto z kolegov tomu nezodpovedá.',
    noColleagues: 'Zatiaľ sa nepripojili žiadni ďalší kolegovia.',
    loadingPeople: 'Načítavajú sa kolegovia…',
    peopleFailed: 'Nepodarilo sa nám načítať vašich kolegov. Zavrite toto okno a skúste to znova.',
    owner: 'Vlastník',
    you: '{name} (vy)',
    canUse: 'Môže používať',
    canUseBody: 'Otvoriť a používať aktuálnu verziu.',
    canEdit: 'Môže upravovať',
    canEditBody: 'Meniť ju a to, na čom beží, prostredníctvom jej chatu.',
    canView: 'Môže zobrazovať',
    canViewBody: 'Čítať ju a sledovať nové odpovede.',
    canEditFile: 'Meniť ju.',
    removeAccess: 'Odstrániť prístup',
    accessFor: 'Prístup pre {name}',
    everyoneRowBody: 'Ktokoľvek v spoločnosti ju môže nájsť a otvoriť',
    yourAccess: 'Váš prístup',
    sharedBy: '{name} to s vami zdieľal(a)',
    sharedWithYou: 'Bolo to s vami zdieľané',
    ownerDecides: '{name} je vlastníkom a rozhoduje, kto to môže otvoriť.',
    runsOn: 'Beží na',
    runsOnHelp:
      'Tieto pripojenia patria k položke {thing}. Ľudia, ktorí ju otvoria, uvidia, čo zobrazuje, ale nikdy nezískajú prístup k účtom.',
    runsOnViewerHelp:
      'Tieto pripojenia používate prostredníctvom položky {thing}. Nezískate prístup k účtom a položka nikdy nepoužije vaše účty.',
    runsOnEmpty: 'Zatiaľ nepoužíva žiadne pripojenia.',
    runsOnFailed: 'Nepodarilo sa nám načítať, na čom beží.',
    yours: 'Vaše',
    from: 'Od {name}',
    fromColleague: 'Od kolegu',
    reads: 'Číta {list}',
    readsNothing: 'Zatiaľ nič nečíta',
    canChange: 'môže vykonávať zmeny',
    paused: 'Pozastavené',
    pausedBody:
      'Prístup používateľa {name} sa skončil, takže položka {thing} momentálne nemôže čítať {connection} pre nikoho. Sama neprepne na účet niekoho iného.',
    pausedByColleague:
      'Prístup, ktorý používala, sa skončil, takže položka {thing} momentálne nemôže čítať {connection} pre nikoho. Sama neprepne na účet niekoho iného.',
    fixInChat: 'Opraviť v chate',
    fixInChatBody: 'Požiadajte Chat, aby namiesto toho použil váš vlastný prístup.',
    chatHint: 'Ak chcete pridať alebo zmeniť pripojenie, požiadajte o to v jeho chate.',
    changeWithChat: 'Zmeniť pomocou Chatu',
    editInChat: 'Upraviť v chate',
    unnamedConnection: 'Nepomenované pripojenie',
    retry: 'Skúsiť znova',
    cancel: 'Zrušiť',
    save: 'Uložiť',
    done: 'Hotovo',
    saving: 'Ukladá sa…',
    statusPrivate: 'Otvoriť to môžete iba vy.',
    statusCompany: 'Môže to otvoriť každý v spoločnosti {company}.',
    statusPeople: 'Zdieľané s používateľmi: {people}.',
    statusPeopleEmpty: 'Pridajte ľudí, s ktorými to chcete zdieľať.',
    statusPending: 'Kým neuložíte zmeny, nič sa nezmení.',
    theApp: 'aplikácia',
    theAgent: 'agent',
    theSkill: 'zručnosť',
    theResult: 'výsledok',
    theItem: 'položka',
    untitled: 'Bez názvu',
    updated: 'Zdieľanie bolo aktualizované.',
    agentNoOwner: 'Vlastník odišiel, preto agent zostáva zdieľaný. Vypnite ho alebo archivujte.',
    agentPersonal:
      'Táto voľba nastaví agenta ako súkromného pre jeho vlastníka: vypne sa, čakajúce behy sa skončia a ďalší beh vytvorí nový výsledok.',
    changesPrompt:
      'Pomôž mi zmeniť, na čom {kind} {id} ({name}) funguje. Najprv si prečítaj jeho pripojenia a potom navrhni zmenu, ktorú schválim.',
    pausedCodeChanged:
      'Niekto zmenil položku {thing} po pripojení {connection} používateľom {name}. Pre všetkých je pozastavená, kým {name} pripojenie v jej chate nepotvrdí.',
    pausedCodeChangedColleague:
      'Niekto zmenil položku {thing} po potvrdení jej pripojenia k {connection}. Pre všetkých je pozastavená, kým sa pripojenie v jej chate znova nepotvrdí.',
    pausedCodeChangedYou:
      'Niekto zmenil položku {thing} po vašom pripojení {connection}. Pre všetkých je pozastavená, kým pripojenie v jej chate nepotvrdíte.',
    fixInChatConfirmBody: 'Požiadajte Chat, aby to potvrdil s vaším prístupom.',
  },
  sl: {
    eyebrowShare: 'Daj v skupno rabo',
    eyebrowShared: 'V skupni rabi z vami',
    close: 'Zapri',
    share: 'Deli',
    shared: 'V skupni rabi',
    shareWith: 'Deli: {audience}',
    whoTitle: 'Kdo ga lahko odpre',
    onlyYou: 'Samo vi',
    onlyYouBody: 'Nihče drug ga ne more odpreti',
    everyone: 'Vsi v podjetju {company}',
    everyoneBody: 'Odpre ga lahko kdorkoli v podjetju',
    specificPeople: 'Določene osebe',
    specificPeopleBody: 'Izberite, kdo ga lahko odpre',
    companyNeedsEdit: 'Samo osebe, ki ga lahko urejajo, ga lahko dajo v skupno rabo z vsemi.',
    privateNeedsOwner: 'Samo lastnik ali skrbnik ga lahko znova naredi zasebnega.',
    skillCompany: 'Veščina je na voljo vsem, ko je objavljena.',
    peopleCount: 'one:oseba|two:osebi|few:osebe|other:oseb',
    addPeople: 'Dodajte osebe po imenu ali e-pošti',
    noMatches: 'Noben sodelavec se ne ujema z iskanjem.',
    noColleagues: 'Pridružil se še ni noben drug sodelavec.',
    loadingPeople: 'Nalaganje sodelavcev…',
    peopleFailed: 'Sodelavcev ni bilo mogoče naložiti. Zaprite in poskusite znova.',
    owner: 'Lastnik',
    you: '{name} (vi)',
    canUse: 'Lahko uporablja',
    canUseBody: 'Odpre ga in uporablja trenutno različico.',
    canEdit: 'Lahko ureja',
    canEditBody: 'Spreminja ga in to, kar uporablja, prek njegovega klepeta.',
    canView: 'Lahko si ogleda',
    canViewBody: 'Prebere ga in spremlja nove odgovore.',
    canEditFile: 'Spremeni ga.',
    removeAccess: 'Odstrani dostop',
    accessFor: 'Dostop za {name}',
    everyoneRowBody: 'Kdorkoli v podjetju ga lahko najde in odpre',
    yourAccess: 'Vaš dostop',
    sharedBy: '{name} ga je dal v skupno rabo z vami',
    sharedWithYou: 'Dali so ga v skupno rabo z vami',
    ownerDecides: '{name} je lastnik in določa, kdo ga lahko odpre.',
    runsOn: 'Uporablja',
    runsOnHelp:
      'Te povezave pripadajo {thing}. Osebe, ki ga odprejo, vidijo, kaj prikazuje, vendar nikoli ne dobijo dostopa do računov.',
    runsOnViewerHelp:
      'Te povezave uporabljate prek {thing}. Dostopa do računov ne dobite, vaši računi pa se pri tem nikoli ne uporabljajo.',
    runsOnEmpty: 'Še ne uporablja nobenih povezav.',
    runsOnFailed: 'Podatkov o tem, kar uporablja, ni bilo mogoče naložiti.',
    yours: 'Vaše',
    from: 'Od {name}',
    fromColleague: 'Od sodelavca',
    reads: 'Bere {list}',
    readsNothing: 'Zaenkrat ne bere ničesar',
    canChange: 'lahko spreminja',
    paused: 'Začasno ustavljeno',
    pausedBody:
      'Dostop osebe {name} se je končal, zato {thing} trenutno nihče ne more uporabljati za branje povezave {connection}. Sam od sebe ne bo preklopil na račun druge osebe.',
    pausedByColleague:
      'Dostop, ki ga je uporabljal, se je končal, zato {thing} trenutno nihče ne more uporabljati za branje povezave {connection}. Sam od sebe ne bo preklopil na račun druge osebe.',
    fixInChat: 'Popravi v klepetu',
    fixInChatBody: 'Prosite Chat, naj namesto tega uporabi vaš dostop.',
    chatHint: 'Če želite dodati ali spremeniti povezavo, to napišite v njegovem klepetu.',
    changeWithChat: 'Spremeni s Chatom',
    editInChat: 'Uredi v klepetu',
    unnamedConnection: 'Neimenovana povezava',
    retry: 'Poskusi znova',
    cancel: 'Prekliči',
    save: 'Shrani',
    done: 'Končano',
    saving: 'Shranjevanje…',
    statusPrivate: 'Odprete ga lahko samo vi.',
    statusCompany: 'Odpre ga lahko vsak v {company}.',
    statusPeople: 'V skupni rabi z: {people}.',
    statusPeopleEmpty: 'Dodajte osebe, da ga daste v skupno rabo.',
    statusPending: 'Nič se ne bo spremenilo, dokler ne shranite.',
    theApp: 'aplikacija',
    theAgent: 'agent',
    theSkill: 'veščina',
    theResult: 'rezultat',
    theItem: 'to',
    untitled: 'Brez naslova',
    updated: 'Deljenje je posodobljeno.',
    agentNoOwner:
      'Lastnik je odšel, zato agent ostane v skupni rabi. Izklopite ga ali arhivirajte.',
    agentPersonal:
      'Ta izbira naredi agenta zasebnega za lastnika: izklopi se, čakajoča izvajanja se končajo, naslednje pa začne nov rezultat.',
    changesPrompt:
      'Pomagaj mi spremeniti, na čem se izvaja {kind} {id} ({name}). Najprej preberi njegove povezave, nato predlagaj spremembo, ki jo bom odobril.',
    pausedCodeChanged:
      'Nekdo je spremenil {thing}, potem ko je {name} povezal(a) {connection}. Za vse je ustavljeno, dokler {name} ne potrdi povezave v njegovem klepetu.',
    pausedCodeChangedColleague:
      'Nekdo je spremenil {thing}, potem ko je bila potrjena njegova povezava z {connection}. Za vse je ustavljeno, dokler povezava ni znova potrjena v njegovem klepetu.',
    pausedCodeChangedYou:
      'Nekdo je spremenil {thing}, potem ko ste povezali {connection}. Za vse je ustavljeno, dokler povezave ne potrdite v njegovem klepetu.',
    fixInChatConfirmBody: 'Prosite Chat, naj to potrdi z vašim dostopom.',
  },
  es: {
    eyebrowShare: 'Compartir',
    eyebrowShared: 'Compartido contigo',
    close: 'Cerrar',
    share: 'Compartir',
    shared: 'Compartida',
    shareWith: 'Compartir: {audience}',
    whoTitle: 'Quién puede abrirlo',
    onlyYou: 'Solo tú',
    onlyYouBody: 'Nadie más puede abrirlo',
    everyone: 'Todos en {company}',
    everyoneBody: 'Cualquiera de la empresa puede abrirlo',
    specificPeople: 'Personas concretas',
    specificPeopleBody: 'Elige quién puede abrirlo',
    companyNeedsEdit: 'Solo quienes pueden editarlo pueden compartirlo con toda la empresa.',
    privateNeedsOwner: 'Solo su propietario o un administrador pueden volver a hacerlo privado.',
    skillCompany: 'Un Skill llega a todos cuando se publica.',
    peopleCount: 'one:persona|other:personas',
    addPeople: 'Añadir personas por nombre o correo electrónico',
    noMatches: 'No hay ningún colega que coincida.',
    noColleagues: 'Aún no se ha unido ningún otro colega.',
    loadingPeople: 'Cargando colegas…',
    peopleFailed: 'No se pudieron cargar tus colegas. Cierra y vuelve a intentarlo.',
    owner: 'Propietario',
    you: '{name} (tú)',
    canUse: 'Puede usarlo',
    canUseBody: 'Abrirlo y usar la versión actual.',
    canEdit: 'Puede editarlo',
    canEditBody: 'Cambiarlo y modificar lo que ejecuta mediante su chat.',
    canView: 'Puede verlo',
    canViewBody: 'Leerlo y seguir las nuevas respuestas.',
    canEditFile: 'Modificarlo.',
    removeAccess: 'Quitar acceso',
    accessFor: 'Acceso de {name}',
    everyoneRowBody: 'Cualquiera de la empresa puede encontrarlo y abrirlo',
    yourAccess: 'Tu acceso',
    sharedBy: '{name} lo ha compartido contigo',
    sharedWithYou: 'Se ha compartido contigo',
    ownerDecides: '{name} es su propietario y decide quién puede abrirlo.',
    runsOn: 'Se ejecuta en',
    runsOnHelp:
      'Estas conexiones pertenecen a {thing}. Quienes lo abran verán lo que muestra, pero nunca tendrán acceso a las cuentas.',
    runsOnViewerHelp:
      'Usas estas conexiones a través de {thing}. No tienes acceso a las cuentas y nunca usa las tuyas.',
    runsOnEmpty: 'Aún no usa ninguna conexión.',
    runsOnFailed: 'No se pudo cargar en qué se ejecuta.',
    yours: 'Tuyo',
    from: 'De {name}',
    fromColleague: 'De un colega',
    reads: 'Lee {list}',
    readsNothing: 'Aún no lee nada',
    canChange: 'puede hacer cambios',
    paused: 'Pausado',
    pausedBody:
      'El acceso de {name} ha terminado, así que {thing} no puede leer {connection} para nadie en este momento. No cambiará automáticamente a la cuenta de otra persona.',
    pausedByColleague:
      'El acceso que usaba ha terminado, así que {thing} no puede leer {connection} para nadie en este momento. No cambiará automáticamente a la cuenta de otra persona.',
    fixInChat: 'Solucionar en el chat',
    fixInChatBody: 'Pide a Chat que use tu propio acceso.',
    chatHint: 'Para añadir o cambiar una conexión, pídeselo en su chat.',
    changeWithChat: 'Cambiar con Chat',
    editInChat: 'Editar en el chat',
    unnamedConnection: 'Conexión sin nombre',
    retry: 'Intentar de nuevo',
    cancel: 'Cancelar',
    save: 'Guardar',
    done: 'Listo',
    saving: 'Guardando…',
    statusPrivate: 'Solo tú puedes abrirlo.',
    statusCompany: 'Toda la empresa ({company}) puede abrirlo.',
    statusPeople: 'Compartido con {people}.',
    statusPeopleEmpty: 'Añade personas para compartirlo.',
    statusPending: 'No cambiará nada hasta que guardes.',
    theApp: 'la App',
    theAgent: 'el Agente',
    theSkill: 'el Skill',
    theResult: 'el resultado',
    theItem: 'el elemento',
    untitled: 'Sin título',
    updated: 'Uso compartido actualizado.',
    agentNoOwner:
      'Su propietario se fue, así que el agente sigue compartido. Desactívalo o archívalo.',
    agentPersonal:
      'Esta opción hace que el agente sea privado para su propietario: se desactiva, las ejecuciones pendientes finalizan y la siguiente inicia un resultado nuevo.',
    changesPrompt:
      'Ayúdame a cambiar en qué se basa {kind} {id} ({name}). Lee primero sus conexiones y, después, propón el cambio para que yo lo apruebe.',
    pausedCodeChanged:
      'Alguien cambió {thing} después de que {name} conectara {connection}. Está en pausa para todos hasta que {name} confirme la conexión en su chat.',
    pausedCodeChangedColleague:
      'Alguien cambió {thing} después de confirmar su conexión con {connection}. Está en pausa para todos hasta que se vuelva a confirmar la conexión en su chat.',
    pausedCodeChangedYou:
      'Alguien cambió {thing} después de que conectaras {connection}. Está en pausa para todos hasta que confirmes la conexión en su chat.',
    fixInChatConfirmBody: 'Pide al chat que lo confirme con tu acceso.',
  },
  sv: {
    eyebrowShare: 'Dela',
    eyebrowShared: 'Delad med dig',
    close: 'Stäng',
    share: 'Dela',
    shared: 'Delad',
    shareWith: 'Dela: {audience}',
    whoTitle: 'Vem kan öppna den',
    onlyYou: 'Bara du',
    onlyYouBody: 'Ingen annan kan öppna den',
    everyone: 'Alla på {company}',
    everyoneBody: 'Alla i företaget kan öppna den',
    specificPeople: 'Utvalda personer',
    specificPeopleBody: 'Välj vem som kan öppna den',
    companyNeedsEdit: 'Bara de som kan redigera den kan dela den med alla.',
    privateNeedsOwner: 'Bara ägaren eller en administratör kan göra den privat igen.',
    skillCompany: 'En skill når alla när den publiceras.',
    peopleCount: 'one:person|other:personer',
    addPeople: 'Lägg till personer med namn eller e-post',
    noMatches: 'Ingen kollega matchar.',
    noColleagues: 'Inga andra kollegor har gått med ännu.',
    loadingPeople: 'Hämtar kollegor…',
    peopleFailed: 'Vi kunde inte hämta dina kollegor. Stäng och försök igen.',
    owner: 'Ägare',
    you: '{name} (du)',
    canUse: 'Kan använda',
    canUseBody: 'Öppna den och använda den senaste versionen.',
    canEdit: 'Kan redigera',
    canEditBody: 'Ändra den och vad den körs på, i dess chatt.',
    canView: 'Kan läsa',
    canViewBody: 'Läsa den och följa nya svar.',
    canEditFile: 'Ändra den.',
    removeAccess: 'Ta bort åtkomst',
    accessFor: 'Åtkomst för {name}',
    everyoneRowBody: 'Alla i företaget kan hitta och öppna den',
    yourAccess: 'Din åtkomst',
    sharedBy: '{name} delade den med dig',
    sharedWithYou: 'Den delades med dig',
    ownerDecides: '{name} äger den och bestämmer vem som kan öppna den.',
    runsOn: 'Körs på',
    runsOnHelp:
      'Anslutningarna tillhör {thing}. De som öppnar den ser vad den visar, men får aldrig tillgång till kontona.',
    runsOnViewerHelp:
      'Du använder anslutningarna genom {thing}. Du får inte tillgång till kontona, och den använder aldrig dina.',
    runsOnEmpty: 'Den använder inga anslutningar ännu.',
    runsOnFailed: 'Vi kunde inte hämta vad den körs på.',
    yours: 'Din',
    from: 'Från {name}',
    fromColleague: 'Från en kollega',
    reads: 'Läser {list}',
    readsNothing: 'Läser inget ännu',
    canChange: 'kan göra ändringar',
    paused: 'Pausad',
    pausedBody:
      '{name}s åtkomst har upphört, så {thing} kan inte läsa {connection} för någon just nu. Den byter aldrig till någon annans konto på egen hand.',
    pausedByColleague:
      'Åtkomsten den använde har upphört, så {thing} kan inte läsa {connection} för någon just nu. Den byter aldrig till någon annans konto på egen hand.',
    fixInChat: 'Åtgärda i chatten',
    fixInChatBody: 'Be Chat att använda din egen åtkomst i stället.',
    chatHint: 'Be i dess chatt för att lägga till eller ändra en anslutning.',
    changeWithChat: 'Ändra med Chat',
    editInChat: 'Redigera i chatten',
    unnamedConnection: 'Namnlös anslutning',
    retry: 'Försök igen',
    cancel: 'Avbryt',
    save: 'Spara',
    done: 'Klar',
    saving: 'Sparar…',
    statusPrivate: 'Bara du kan öppna den.',
    statusCompany: 'Alla på {company} kan öppna den.',
    statusPeople: 'Delad med {people}.',
    statusPeopleEmpty: 'Lägg till personer för att dela den.',
    statusPending: 'Inget ändras förrän du sparar.',
    theApp: 'appen',
    theAgent: 'agenten',
    theSkill: 'skillen',
    theResult: 'resultatet',
    theItem: 'den',
    untitled: 'Namnlös',
    updated: 'Delningen är uppdaterad.',
    agentNoOwner:
      'Ägaren har slutat, så den förblir delad. Stäng av den eller arkivera den i stället.',
    agentPersonal:
      'Bara du gör den privat för ägaren: den stängs av, väntande körningar avslutas och nästa körning börjar ett nytt resultat.',
    changesPrompt:
      'Hjälp mig ändra vad {kind} {id} ({name}) körs på. Läs dess anslutningar först och föreslå sedan ändringen så att jag kan godkänna den.',
    pausedCodeChanged:
      'Någon har ändrat {thing} efter att {name} anslöt {connection}. Den är pausad för alla tills {name} bekräftar anslutningen i chatten.',
    pausedCodeChangedColleague:
      'Någon har ändrat {thing} efter att anslutningen till {connection} bekräftades. Den är pausad för alla tills anslutningen bekräftas igen i chatten.',
    pausedCodeChangedYou:
      'Någon har ändrat {thing} efter att du anslöt {connection}. Den är pausad för alla tills du bekräftar anslutningen i chatten.',
    fixInChatConfirmBody: 'Be Chat att bekräfta den med din åtkomst.',
  },
};
export default copy;
